import React, { useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  useColorMode,
  Icon,
  Flex,
  Container,
} from "@chakra-ui/react";
import { brandConfig, getColor } from "../brandConfig";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

// ─── LOGO 1: TMS Hexagon Chain ───────────────────────────────────────
// Blockchain hexagons forming "TMS" monogram
const Logo1 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hex-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5AC8FA" />
      </linearGradient>
      <linearGradient id="hex-grad-dark" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0051D0" />
        <stop offset="100%" stopColor="#007AFF" />
      </linearGradient>
      <filter id="glow1">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Outer hexagon ring */}
    <polygon
      points="100,10 178,45 178,115 100,150 22,115 22,45"
      fill="none"
      stroke="url(#hex-grad)"
      strokeWidth="2.5"
      opacity="0.6"
    />

    {/* Inner hexagon */}
    <polygon
      points="100,30 160,55 160,105 100,130 40,105 40,55"
      fill="url(#hex-grad)"
      opacity="0.1"
    />
    <polygon
      points="100,30 160,55 160,105 100,130 40,105 40,55"
      fill="none"
      stroke="url(#hex-grad)"
      strokeWidth="1.5"
    />

    {/* Chain link connectors - top left */}
    <line x1="22" y1="45" x2="8" y2="35" stroke="#007AFF" strokeWidth="1.5" opacity="0.4" />
    <circle cx="8" cy="35" r="3" fill="#007AFF" opacity="0.4" />

    {/* Chain link connectors - top right */}
    <line x1="178" y1="45" x2="192" y2="35" stroke="#5AC8FA" strokeWidth="1.5" opacity="0.4" />
    <circle cx="192" cy="35" r="3" fill="#5AC8FA" opacity="0.4" />

    {/* Chain link connectors - bottom */}
    <line x1="100" y1="150" x2="100" y2="168" stroke="#007AFF" strokeWidth="1.5" opacity="0.4" />
    <circle cx="100" cy="168" r="3" fill="#007AFF" opacity="0.4" />

    {/* TMS Monogram */}
    <text
      x="100"
      y="92"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="36"
      fill="url(#hex-grad)"
      filter="url(#glow1)"
      letterSpacing="4"
    >
      TMS
    </text>

    {/* Subtitle */}
    <text
      x="100"
      y="178"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="3"
    >
      TOM MILLER SERVICES
    </text>
  </svg>
);

// ─── LOGO 2: DTMS Neural Shield ─────────────────────────────────────
// Shield with neural network nodes — "Dr Thomas Miller Services"
const Logo2 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="shield-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="50%" stopColor="#5856D6" />
        <stop offset="100%" stopColor="#AF52DE" />
      </linearGradient>
      <linearGradient id="neural-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#5AC8FA" />
        <stop offset="100%" stopColor="#007AFF" />
      </linearGradient>
      <filter id="glow2">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Shield shape */}
    <path
      d="M100,15 L170,40 L170,95 Q170,140 100,170 Q30,140 30,95 L30,40 Z"
      fill="url(#shield-grad)"
      opacity="0.08"
    />
    <path
      d="M100,15 L170,40 L170,95 Q170,140 100,170 Q30,140 30,95 L30,40 Z"
      fill="none"
      stroke="url(#shield-grad)"
      strokeWidth="2"
    />

    {/* Neural network nodes */}
    {/* Layer 1 */}
    <circle cx="65" cy="60" r="4" fill="#5AC8FA" filter="url(#glow2)" />
    <circle cx="135" cy="60" r="4" fill="#5AC8FA" filter="url(#glow2)" />
    <circle cx="100" cy="45" r="4" fill="#5AC8FA" filter="url(#glow2)" />

    {/* Layer 2 */}
    <circle cx="75" cy="90" r="5" fill="#007AFF" filter="url(#glow2)" />
    <circle cx="125" cy="90" r="5" fill="#007AFF" filter="url(#glow2)" />

    {/* Layer 3 */}
    <circle cx="100" cy="120" r="5" fill="#5856D6" filter="url(#glow2)" />

    {/* Connections */}
    <line x1="65" y1="60" x2="75" y2="90" stroke="#5AC8FA" strokeWidth="1" opacity="0.5" />
    <line x1="65" y1="60" x2="125" y2="90" stroke="#5AC8FA" strokeWidth="0.5" opacity="0.3" />
    <line x1="135" y1="60" x2="125" y2="90" stroke="#5AC8FA" strokeWidth="1" opacity="0.5" />
    <line x1="135" y1="60" x2="75" y2="90" stroke="#5AC8FA" strokeWidth="0.5" opacity="0.3" />
    <line x1="100" y1="45" x2="75" y2="90" stroke="#5AC8FA" strokeWidth="0.8" opacity="0.4" />
    <line x1="100" y1="45" x2="125" y2="90" stroke="#5AC8FA" strokeWidth="0.8" opacity="0.4" />
    <line x1="75" y1="90" x2="100" y2="120" stroke="#007AFF" strokeWidth="1" opacity="0.5" />
    <line x1="125" y1="90" x2="100" y2="120" stroke="#007AFF" strokeWidth="1" opacity="0.5" />

    {/* "Dr" prefix small */}
    <text
      x="100"
      y="148"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="11"
      fill="#AF52DE"
      letterSpacing="1"
    >
      Dr
    </text>

    {/* Brand text */}
    <text
      x="100"
      y="188"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="500"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="2.5"
    >
      DR TOM MILLER SERVICES
    </text>
  </svg>
);

// ─── LOGO 3: Sovereign Data Lock ────────────────────────────────────
// Circuit-board lock — data sovereignty + blockchain
const Logo3 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="lock-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#30D158" />
        <stop offset="50%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5856D6" />
      </linearGradient>
      <filter id="glow3">
        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Circuit traces radiating out */}
    {/* Left traces */}
    <path d="M60,80 L30,80 L30,60" stroke="#007AFF" strokeWidth="1.5" fill="none" opacity="0.3" />
    <circle cx="30" cy="58" r="2.5" fill="#007AFF" opacity="0.4" />
    <path d="M60,100 L20,100" stroke="#5856D6" strokeWidth="1.5" fill="none" opacity="0.3" />
    <circle cx="18" cy="100" r="2.5" fill="#5856D6" opacity="0.4" />
    <path d="M60,120 L30,120 L30,140" stroke="#007AFF" strokeWidth="1.5" fill="none" opacity="0.3" />
    <circle cx="30" cy="142" r="2.5" fill="#007AFF" opacity="0.4" />

    {/* Right traces */}
    <path d="M140,80 L170,80 L170,60" stroke="#007AFF" strokeWidth="1.5" fill="none" opacity="0.3" />
    <circle cx="170" cy="58" r="2.5" fill="#007AFF" opacity="0.4" />
    <path d="M140,100 L180,100" stroke="#5856D6" strokeWidth="1.5" fill="none" opacity="0.3" />
    <circle cx="182" cy="100" r="2.5" fill="#5856D6" opacity="0.4" />
    <path d="M140,120 L170,120 L170,140" stroke="#007AFF" strokeWidth="1.5" fill="none" opacity="0.3" />
    <circle cx="170" cy="142" r="2.5" fill="#007AFF" opacity="0.4" />

    {/* Lock shackle */}
    <path
      d="M75,70 L75,50 Q75,30 100,30 Q125,30 125,50 L125,70"
      fill="none"
      stroke="url(#lock-grad)"
      strokeWidth="3"
      strokeLinecap="round"
    />

    {/* Lock body */}
    <rect
      x="60"
      y="70"
      width="80"
      height="65"
      rx="8"
      fill="url(#lock-grad)"
      opacity="0.12"
    />
    <rect
      x="60"
      y="70"
      width="80"
      height="65"
      rx="8"
      fill="none"
      stroke="url(#lock-grad)"
      strokeWidth="2"
    />

    {/* Keyhole / blockchain symbol */}
    <circle cx="100" cy="97" r="8" fill="none" stroke="#007AFF" strokeWidth="2" filter="url(#glow3)" />
    <rect x="97" y="103" width="6" height="14" rx="2" fill="#007AFF" filter="url(#glow3)" />

    {/* TMS inside */}
    <text
      x="100"
      y="160"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="20"
      fill="url(#lock-grad)"
      letterSpacing="4"
    >
      TMS
    </text>

    {/* Subtitle */}
    <text
      x="100"
      y="185"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="8"
      fill="#8E8E93"
      letterSpacing="2"
    >
      SOVEREIGN DATA SOLUTIONS
    </text>
  </svg>
);

// ─── LOGO 4: Medical Cross + Blockchain ─────────────────────────────
// Healthcare cross with connected nodes — Dr Thomas Miller
const Logo4 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="med-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#30D158" />
      </linearGradient>
      <linearGradient id="med-grad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#5AC8FA" />
        <stop offset="100%" stopColor="#34C759" />
      </linearGradient>
      <filter id="glow4">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Outer circle */}
    <circle
      cx="100"
      cy="85"
      r="65"
      fill="none"
      stroke="url(#med-grad)"
      strokeWidth="1.5"
      opacity="0.3"
      strokeDasharray="4 4"
    />

    {/* Medical cross */}
    <rect x="85" y="40" width="30" height="90" rx="4" fill="url(#med-grad)" opacity="0.15" />
    <rect x="55" y="70" width="90" height="30" rx="4" fill="url(#med-grad)" opacity="0.15" />

    {/* Cross outline */}
    <path
      d="M85,44 L85,70 L59,70 L59,96 L85,96 L85,126 L115,126 L115,96 L141,96 L141,70 L115,70 L115,44 Z"
      fill="none"
      stroke="url(#med-grad)"
      strokeWidth="2"
    />

    {/* Blockchain nodes at cross tips */}
    <circle cx="100" cy="38" r="5" fill="#007AFF" filter="url(#glow4)" />
    <circle cx="100" cy="132" r="5" fill="#30D158" filter="url(#glow4)" />
    <circle cx="53" cy="85" r="5" fill="#5AC8FA" filter="url(#glow4)" />
    <circle cx="147" cy="85" r="5" fill="#34C759" filter="url(#glow4)" />

    {/* Node connections - forming chain */}
    <path d="M100,33 Q80,20 55,30" stroke="#007AFF" strokeWidth="1" fill="none" opacity="0.3" />
    <circle cx="53" cy="30" r="2.5" fill="#007AFF" opacity="0.3" />
    <path d="M100,33 Q120,20 145,30" stroke="#5AC8FA" strokeWidth="1" fill="none" opacity="0.3" />
    <circle cx="147" cy="30" r="2.5" fill="#5AC8FA" opacity="0.3" />

    {/* Center "Dr" */}
    <text
      x="100"
      y="88"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="22"
      fill="url(#med-grad)"
      filter="url(#glow4)"
    >
      Dr.TM
    </text>

    {/* Brand text */}
    <text
      x="100"
      y="170"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="500"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="2"
    >
      DR TOM MILLER
    </text>
    <text
      x="100"
      y="185"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="8"
      fill="#6E6E73"
      letterSpacing="2.5"
    >
      HEALTH · AI · BLOCKCHAIN
    </text>
  </svg>
);

// ─── LOGO 5: TMS Circuit Monogram ───────────────────────────────────
// Clean, modern monogram with integrated circuit traces
const Logo5 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="mono-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="40%" stopColor="#5856D6" />
        <stop offset="100%" stopColor="#FF2D55" />
      </linearGradient>
      <linearGradient id="mono-grad2" x1="100%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#5AC8FA" />
        <stop offset="100%" stopColor="#007AFF" />
      </linearGradient>
      <filter id="glow5">
        <feGaussianBlur stdDeviation="4" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Background circle */}
    <circle
      cx="100"
      cy="88"
      r="72"
      fill="url(#mono-grad)"
      opacity="0.06"
    />
    <circle
      cx="100"
      cy="88"
      r="72"
      fill="none"
      stroke="url(#mono-grad)"
      strokeWidth="1.5"
      opacity="0.4"
    />

    {/* Circuit traces coming off the circle */}
    {/* Top */}
    <line x1="100" y1="16" x2="100" y2="5" stroke="#5856D6" strokeWidth="1.5" opacity="0.4" />
    <circle cx="100" cy="4" r="2" fill="#5856D6" opacity="0.5" />

    {/* Top right */}
    <line x1="152" y1="38" x2="165" y2="25" stroke="#007AFF" strokeWidth="1.5" opacity="0.4" />
    <circle cx="166" cy="24" r="2" fill="#007AFF" opacity="0.5" />

    {/* Right */}
    <line x1="172" y1="88" x2="185" y2="88" stroke="#5AC8FA" strokeWidth="1.5" opacity="0.4" />
    <circle cx="187" cy="88" r="2" fill="#5AC8FA" opacity="0.5" />

    {/* Bottom right */}
    <line x1="152" y1="138" x2="165" y2="151" stroke="#007AFF" strokeWidth="1.5" opacity="0.4" />
    <circle cx="166" cy="152" r="2" fill="#007AFF" opacity="0.5" />

    {/* Bottom left */}
    <line x1="48" y1="138" x2="35" y2="151" stroke="#5856D6" strokeWidth="1.5" opacity="0.4" />
    <circle cx="34" cy="152" r="2" fill="#5856D6" opacity="0.5" />

    {/* Left */}
    <line x1="28" y1="88" x2="15" y2="88" stroke="#FF2D55" strokeWidth="1.5" opacity="0.4" />
    <circle cx="13" cy="88" r="2" fill="#FF2D55" opacity="0.5" />

    {/* Top left */}
    <line x1="48" y1="38" x2="35" y2="25" stroke="#AF52DE" strokeWidth="1.5" opacity="0.4" />
    <circle cx="34" cy="24" r="2" fill="#AF52DE" opacity="0.5" />

    {/* Bold "T" */}
    <text
      x="66"
      y="100"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="800"
      fontSize="52"
      fill="url(#mono-grad)"
      filter="url(#glow5)"
    >
      T
    </text>

    {/* Smaller overlapping "M" */}
    <text
      x="105"
      y="100"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="600"
      fontSize="36"
      fill="url(#mono-grad2)"
      opacity="0.9"
    >
      M
    </text>

    {/* Small "S" */}
    <text
      x="140"
      y="105"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="500"
      fontSize="24"
      fill="#5AC8FA"
      opacity="0.7"
    >
      S
    </text>

    {/* Brand text */}
    <text
      x="100"
      y="180"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="500"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="3"
    >
      TOM MILLER SERVICES
    </text>
    <text
      x="100"
      y="195"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      BUILD · SECURE · SCALE
    </text>
  </svg>
);

// ─── LOGO 6: Graduation Cap + Blockchain ─────────────────────────────
// Mortarboard hat with chain-link tassel — "Dr Thomas Miller Services"
const Logo6 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="cap-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5856D6" />
      </linearGradient>
      <linearGradient id="tassel-grad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FFD60A" />
        <stop offset="100%" stopColor="#FF9F0A" />
      </linearGradient>
      <filter id="glow6">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Graduation cap - mortarboard top (diamond/rhombus) */}
    <polygon
      points="100,22 170,52 100,82 30,52"
      fill="url(#cap-grad)"
      opacity="0.9"
    />
    <polygon
      points="100,22 170,52 100,82 30,52"
      fill="none"
      stroke="#5AC8FA"
      strokeWidth="1"
      opacity="0.4"
    />

    {/* Cap brim/base */}
    <path
      d="M50,52 L50,80 Q50,95 100,105 Q150,95 150,80 L150,52"
      fill="url(#cap-grad)"
      opacity="0.15"
    />
    <path
      d="M50,52 L50,80 Q50,95 100,105 Q150,95 150,80 L150,52"
      fill="none"
      stroke="url(#cap-grad)"
      strokeWidth="1.5"
      opacity="0.5"
    />

    {/* Tassel string from center */}
    <line x1="100" y1="52" x2="155" y2="52" stroke="#FFD60A" strokeWidth="1.5" opacity="0.7" />
    <line x1="155" y1="52" x2="155" y2="85" stroke="url(#tassel-grad)" strokeWidth="2" />

    {/* Tassel end - blockchain nodes */}
    <circle cx="155" cy="88" r="4" fill="#FFD60A" filter="url(#glow6)" />
    <circle cx="155" cy="100" r="3" fill="#FF9F0A" opacity="0.7" />
    <circle cx="155" cy="110" r="2" fill="#FF9F0A" opacity="0.5" />
    <line x1="155" y1="92" x2="155" y2="97" stroke="#FFD60A" strokeWidth="1" opacity="0.6" />
    <line x1="155" y1="103" x2="155" y2="108" stroke="#FF9F0A" strokeWidth="1" opacity="0.4" />

    {/* Blockchain chain links along the bottom */}
    <circle cx="60" cy="120" r="3" fill="#007AFF" opacity="0.3" />
    <line x1="63" y1="120" x2="77" y2="120" stroke="#007AFF" strokeWidth="1" opacity="0.3" />
    <circle cx="80" cy="120" r="3" fill="#5856D6" opacity="0.3" />
    <line x1="83" y1="120" x2="97" y2="120" stroke="#5856D6" strokeWidth="1" opacity="0.3" />
    <circle cx="100" cy="120" r="3" fill="#007AFF" opacity="0.3" />
    <line x1="103" y1="120" x2="117" y2="120" stroke="#007AFF" strokeWidth="1" opacity="0.3" />
    <circle cx="120" cy="120" r="3" fill="#5856D6" opacity="0.3" />
    <line x1="123" y1="120" x2="137" y2="120" stroke="#5856D6" strokeWidth="1" opacity="0.3" />
    <circle cx="140" cy="120" r="3" fill="#007AFF" opacity="0.3" />

    {/* "Dr Thomas Miller" text */}
    <text
      x="100"
      y="145"
      textAnchor="middle"
      fontFamily="'Georgia', 'Times New Roman', serif"
      fontWeight="700"
      fontSize="16"
      fill="url(#cap-grad)"
      letterSpacing="1"
    >
      Dr Thomas Miller
    </text>
    <text
      x="100"
      y="162"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="9"
      fill="#8E8E93"
      letterSpacing="3"
    >
      SERVICES
    </text>

    {/* Small tagline */}
    <text
      x="100"
      y="185"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      AI · BLOCKCHAIN · HEALTHCARE
    </text>
  </svg>
);

// ─── LOGO 7: Academic Crest ──────────────────────────────────────────
// University-style seal with mortarboard, shield, and laurels
const Logo7 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="crest-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#0051D0" />
      </linearGradient>
      <linearGradient id="gold-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFD60A" />
        <stop offset="100%" stopColor="#BF8B00" />
      </linearGradient>
      <filter id="glow7">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Outer circle - seal border */}
    <circle cx="100" cy="95" r="75" fill="none" stroke="url(#crest-grad)" strokeWidth="2.5" />
    <circle cx="100" cy="95" r="70" fill="none" stroke="url(#crest-grad)" strokeWidth="0.5" opacity="0.4" />

    {/* Laurel wreath - left */}
    <path d="M40,120 Q30,100 40,80" fill="none" stroke="url(#gold-grad)" strokeWidth="1.5" opacity="0.6" />
    <ellipse cx="36" cy="85" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(-20 36 85)" />
    <ellipse cx="33" cy="95" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(-10 33 95)" />
    <ellipse cx="33" cy="105" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(5 33 105)" />
    <ellipse cx="36" cy="115" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(15 36 115)" />

    {/* Laurel wreath - right */}
    <path d="M160,120 Q170,100 160,80" fill="none" stroke="url(#gold-grad)" strokeWidth="1.5" opacity="0.6" />
    <ellipse cx="164" cy="85" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(20 164 85)" />
    <ellipse cx="167" cy="95" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(10 167 95)" />
    <ellipse cx="167" cy="105" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(-5 167 105)" />
    <ellipse cx="164" cy="115" rx="4" ry="7" fill="url(#gold-grad)" opacity="0.2" transform="rotate(-15 164 115)" />

    {/* Graduation cap in center */}
    <polygon
      points="100,42 145,60 100,78 55,60"
      fill="url(#crest-grad)"
      opacity="0.85"
    />
    <path
      d="M68,60 L68,78 Q68,90 100,96 Q132,90 132,78 L132,60"
      fill="url(#crest-grad)"
      opacity="0.12"
    />
    <path
      d="M68,60 L68,78 Q68,90 100,96 Q132,90 132,78 L132,60"
      fill="none"
      stroke="url(#crest-grad)"
      strokeWidth="1"
      opacity="0.4"
    />

    {/* Tassel */}
    <line x1="100" y1="60" x2="138" y2="60" stroke="#FFD60A" strokeWidth="1.2" opacity="0.6" />
    <line x1="138" y1="60" x2="138" y2="78" stroke="url(#gold-grad)" strokeWidth="1.5" />
    <circle cx="138" cy="80" r="2.5" fill="#FFD60A" />

    {/* Inner shield */}
    <path
      d="M85,100 L85,118 Q85,132 100,138 Q115,132 115,118 L115,100 Z"
      fill="url(#crest-grad)"
      opacity="0.1"
    />
    <path
      d="M85,100 L85,118 Q85,132 100,138 Q115,132 115,118 L115,100 Z"
      fill="none"
      stroke="url(#crest-grad)"
      strokeWidth="1.5"
    />

    {/* "D" inside shield */}
    <text
      x="100"
      y="122"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'Georgia', 'Times New Roman', serif"
      fontWeight="700"
      fontSize="24"
      fill="url(#crest-grad)"
    >
      D
    </text>

    {/* Circular text - top */}
    <path id="topArc7" d="M35,95 A65,65 0 0,1 165,95" fill="none" />
    <text fontSize="7" fill="#8E8E93" letterSpacing="4" fontFamily="'SF Pro Text', 'Inter', sans-serif" fontWeight="500">
      <textPath href="#topArc7" startOffset="50%" textAnchor="middle">
        DR THOMAS MILLER
      </textPath>
    </text>

    {/* Circular text - bottom */}
    <path id="botArc7" d="M40,110 A65,65 0 0,0 160,110" fill="none" />
    <text fontSize="6.5" fill="#6E6E73" letterSpacing="3.5" fontFamily="'SF Pro Text', 'Inter', sans-serif" fontWeight="400">
      <textPath href="#botArc7" startOffset="50%" textAnchor="middle">
        SERVICES
      </textPath>
    </text>

    {/* Bottom tagline */}
    <text
      x="100"
      y="195"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      TECHNOLOGY · RESEARCH · INNOVATION
    </text>
  </svg>
);

// ─── LOGO 8: Mortarboard Monogram ────────────────────────────────────
// Minimalist grad cap sitting on "DTM" lettermark
const Logo8 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dtm-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5AC8FA" />
      </linearGradient>
      <linearGradient id="dtm-gold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFD60A" />
        <stop offset="100%" stopColor="#FF9F0A" />
      </linearGradient>
      <filter id="glow8">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Graduation cap - sitting on top of text */}
    <polygon
      points="100,18 155,40 100,62 45,40"
      fill="url(#dtm-grad)"
      opacity="0.85"
    />
    {/* Cap band */}
    <path
      d="M60,40 L60,55 Q60,65 100,72 Q140,65 140,55 L140,40"
      fill="url(#dtm-grad)"
      opacity="0.1"
    />
    <path
      d="M60,40 L60,55 Q60,65 100,72 Q140,65 140,55 L140,40"
      fill="none"
      stroke="url(#dtm-grad)"
      strokeWidth="1"
      opacity="0.3"
    />

    {/* Gold tassel */}
    <line x1="100" y1="40" x2="148" y2="40" stroke="#FFD60A" strokeWidth="1.2" opacity="0.6" />
    <path d="M148,40 Q152,55 148,70" stroke="url(#dtm-gold)" strokeWidth="1.8" fill="none" />
    <circle cx="147" cy="72" r="3" fill="#FFD60A" filter="url(#glow8)" />
    {/* Tassel fringe */}
    <line x1="144" y1="72" x2="142" y2="80" stroke="#FFD60A" strokeWidth="1" opacity="0.5" />
    <line x1="147" y1="75" x2="147" y2="83" stroke="#FFD60A" strokeWidth="1" opacity="0.5" />
    <line x1="150" y1="72" x2="152" y2="80" stroke="#FFD60A" strokeWidth="1" opacity="0.5" />

    {/* DTM Monogram - large */}
    <text
      x="100"
      y="110"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'Georgia', 'Times New Roman', serif"
      fontWeight="700"
      fontSize="44"
      fill="url(#dtm-grad)"
      filter="url(#glow8)"
      letterSpacing="3"
    >
      DTM
    </text>

    {/* Thin line separator */}
    <line x1="50" y1="135" x2="150" y2="135" stroke="url(#dtm-grad)" strokeWidth="1" opacity="0.3" />

    {/* Full name */}
    <text
      x="100"
      y="152"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="500"
      fontSize="9"
      fill="#8E8E93"
      letterSpacing="2.5"
    >
      DR THOMAS MILLER
    </text>
    <text
      x="100"
      y="166"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="8"
      fill="#6E6E73"
      letterSpacing="4"
    >
      SERVICES
    </text>

    {/* Small dots as separator */}
    <circle cx="85" cy="182" r="1.5" fill="#007AFF" opacity="0.3" />
    <circle cx="100" cy="182" r="1.5" fill="#5856D6" opacity="0.3" />
    <circle cx="115" cy="182" r="1.5" fill="#5AC8FA" opacity="0.3" />
  </svg>
);

// ─── LOGO 9: Scholar's Hex Badge ─────────────────────────────────────
// Hexagonal badge with mortarboard + blockchain nodes
const Logo9 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="scholar-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0051D0" />
        <stop offset="50%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5AC8FA" />
      </linearGradient>
      <linearGradient id="scholar-gold" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FFD60A" />
        <stop offset="100%" stopColor="#BF8B00" />
      </linearGradient>
      <filter id="glow9">
        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Main hexagon badge */}
    <polygon
      points="100,15 162,42 162,118 100,148 38,118 38,42"
      fill="url(#scholar-grad)"
      opacity="0.08"
    />
    <polygon
      points="100,15 162,42 162,118 100,148 38,118 38,42"
      fill="none"
      stroke="url(#scholar-grad)"
      strokeWidth="2.5"
    />
    {/* Inner hex outline */}
    <polygon
      points="100,25 152,48 152,112 100,138 48,112 48,48"
      fill="none"
      stroke="url(#scholar-grad)"
      strokeWidth="0.5"
      opacity="0.3"
    />

    {/* Graduation cap centered in hex */}
    <polygon
      points="100,38 140,54 100,70 60,54"
      fill="url(#scholar-grad)"
      opacity="0.8"
    />

    {/* Cap base */}
    <path
      d="M72,54 L72,68 Q72,78 100,84 Q128,78 128,68 L128,54"
      fill="url(#scholar-grad)"
      opacity="0.12"
    />
    <path
      d="M72,54 L72,68 Q72,78 100,84 Q128,78 128,68 L128,54"
      fill="none"
      stroke="url(#scholar-grad)"
      strokeWidth="1"
      opacity="0.35"
    />

    {/* Gold tassel */}
    <line x1="100" y1="54" x2="135" y2="54" stroke="#FFD60A" strokeWidth="1" opacity="0.6" />
    <line x1="135" y1="54" x2="135" y2="72" stroke="url(#scholar-gold)" strokeWidth="1.5" />
    <circle cx="135" cy="74" r="2.5" fill="#FFD60A" />

    {/* Blockchain nodes at hex vertices */}
    <circle cx="100" cy="15" r="3.5" fill="#5AC8FA" filter="url(#glow9)" />
    <circle cx="162" cy="42" r="3.5" fill="#007AFF" filter="url(#glow9)" />
    <circle cx="162" cy="118" r="3.5" fill="#5AC8FA" filter="url(#glow9)" />
    <circle cx="100" cy="148" r="3.5" fill="#007AFF" filter="url(#glow9)" />
    <circle cx="38" cy="118" r="3.5" fill="#5AC8FA" filter="url(#glow9)" />
    <circle cx="38" cy="42" r="3.5" fill="#007AFF" filter="url(#glow9)" />

    {/* "DTMS" text inside */}
    <text
      x="100"
      y="108"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="800"
      fontSize="26"
      fill="url(#scholar-grad)"
      filter="url(#glow9)"
      letterSpacing="4"
    >
      DTMS
    </text>

    {/* Full text below hex */}
    <text
      x="100"
      y="170"
      textAnchor="middle"
      fontFamily="'Georgia', 'Times New Roman', serif"
      fontWeight="600"
      fontSize="11"
      fill="url(#scholar-grad)"
      letterSpacing="1"
    >
      Dr Thomas Miller Services
    </text>

    {/* Tagline */}
    <text
      x="100"
      y="188"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      BLOCKCHAIN · AI · SOVEREIGN DATA
    </text>
  </svg>
);

// ─── LOGO 10: Professor's Mark ───────────────────────────────────────
// Elegant mortarboard with circuit-trace tassel, stacked layout
const Logo10 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="prof-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="50%" stopColor="#5856D6" />
        <stop offset="100%" stopColor="#AF52DE" />
      </linearGradient>
      <linearGradient id="prof-gold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFD60A" />
        <stop offset="100%" stopColor="#FF9F0A" />
      </linearGradient>
      <filter id="glow10">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Large centered mortarboard */}
    <polygon
      points="100,10 175,45 100,80 25,45"
      fill="url(#prof-grad)"
      opacity="0.9"
    />
    {/* Slight highlight on top face */}
    <polygon
      points="100,10 175,45 100,55 25,45"
      fill="white"
      opacity="0.05"
    />

    {/* Cap base / band */}
    <path
      d="M50,45 L50,70 Q50,88 100,96 Q150,88 150,70 L150,45"
      fill="url(#prof-grad)"
      opacity="0.12"
    />
    <path
      d="M50,45 L50,70 Q50,88 100,96 Q150,88 150,70 L150,45"
      fill="none"
      stroke="url(#prof-grad)"
      strokeWidth="1.5"
      opacity="0.4"
    />

    {/* Circuit-trace tassel */}
    <line x1="100" y1="45" x2="160" y2="45" stroke="#FFD60A" strokeWidth="1.5" opacity="0.7" />
    {/* Tassel becomes a circuit trace */}
    <path
      d="M160,45 L160,65 L170,75 L170,90"
      fill="none"
      stroke="url(#prof-gold)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Circuit nodes along tassel */}
    <circle cx="160" cy="45" r="2" fill="#FFD60A" />
    <circle cx="160" cy="65" r="3" fill="#FFD60A" filter="url(#glow10)" />
    <circle cx="170" cy="75" r="2.5" fill="#FF9F0A" filter="url(#glow10)" />
    <circle cx="170" cy="92" r="3.5" fill="#FFD60A" filter="url(#glow10)" />

    {/* Small circuit nodes floating around cap */}
    <circle cx="30" cy="30" r="2" fill="#007AFF" opacity="0.3" />
    <line x1="30" y1="30" x2="25" y2="45" stroke="#007AFF" strokeWidth="0.8" opacity="0.2" />
    <circle cx="170" cy="25" r="2" fill="#5856D6" opacity="0.3" />
    <line x1="170" y1="25" x2="175" y2="45" stroke="#5856D6" strokeWidth="0.8" opacity="0.2" />
    <circle cx="20" cy="60" r="1.5" fill="#5AC8FA" opacity="0.3" />
    <circle cx="180" cy="60" r="1.5" fill="#AF52DE" opacity="0.3" />

    {/* "Dr." in elegant serif */}
    <text
      x="100"
      y="118"
      textAnchor="middle"
      fontFamily="'Georgia', 'Times New Roman', serif"
      fontWeight="400"
      fontStyle="italic"
      fontSize="18"
      fill="url(#prof-grad)"
      opacity="0.7"
    >
      Dr.
    </text>

    {/* "Thomas Miller" bold */}
    <text
      x="100"
      y="142"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="20"
      fill="url(#prof-grad)"
      filter="url(#glow10)"
      letterSpacing="2"
    >
      THOMAS MILLER
    </text>

    {/* Decorative line */}
    <line x1="40" y1="155" x2="160" y2="155" stroke="url(#prof-grad)" strokeWidth="0.8" opacity="0.3" />

    {/* "SERVICES" */}
    <text
      x="100"
      y="170"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="6"
    >
      SERVICES
    </text>

    {/* Dots */}
    <text
      x="100"
      y="192"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      AI · BLOCKCHAIN · HEALTHCARE · DATA
    </text>
  </svg>
);

// ─── LOGO 11: Clean Cap Minimal ──────────────────────────────────────
// Flat mortarboard silhouette, no fuss, stacked text
const Logo11 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="g11" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5AC8FA" />
      </linearGradient>
    </defs>

    {/* Simple flat mortarboard — just the diamond */}
    <polygon points="100,14 158,38 100,62 42,38" fill="url(#g11)" />

    {/* Simple rectangular band */}
    <rect x="60" y="50" width="80" height="16" rx="2" fill="url(#g11)" opacity="0.2" />

    {/* Single tassel line + circle */}
    <line x1="140" y1="38" x2="154" y2="62" stroke="#FFD60A" strokeWidth="2" strokeLinecap="round" />
    <circle cx="154" cy="64" r="4" fill="#FFD60A" />

    {/* Dr. */}
    <text
      x="100" y="95"
      textAnchor="middle"
      fontFamily="'Georgia', serif"
      fontStyle="italic"
      fontWeight="400"
      fontSize="16"
      fill="url(#g11)"
      opacity="0.7"
    >
      Dr.
    </text>

    {/* THOMAS MILLER */}
    <text
      x="100" y="118"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="20"
      fill="url(#g11)"
      letterSpacing="2"
    >
      THOMAS MILLER
    </text>

    {/* Line */}
    <line x1="45" y1="130" x2="155" y2="130" stroke="url(#g11)" strokeWidth="0.8" opacity="0.25" />

    {/* SERVICES */}
    <text
      x="100" y="146"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="6"
    >
      SERVICES
    </text>

    {/* Tagline */}
    <text
      x="100" y="170"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      AI · BLOCKCHAIN · HEALTHCARE · DATA
    </text>
  </svg>
);

// ─── LOGO 12: Outlined Cap Badge ─────────────────────────────────────
// Just an outlined cap shape with a single accent dot, very clean
const Logo12 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="g12" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5856D6" />
      </linearGradient>
    </defs>

    {/* Outlined mortarboard — stroke only */}
    <polygon
      points="100,12 162,38 100,64 38,38"
      fill="none"
      stroke="url(#g12)"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />

    {/* Cap base — simple open trapezoid */}
    <path
      d="M58,38 L58,58 Q58,68 100,74 Q142,68 142,58 L142,38"
      fill="none"
      stroke="url(#g12)"
      strokeWidth="1.5"
      opacity="0.4"
    />

    {/* Gold tassel — single clean stroke */}
    <path
      d="M100,38 L150,38 L150,60"
      fill="none"
      stroke="#FFD60A"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="150" cy="63" r="3.5" fill="#FFD60A" />

    {/* Dr. */}
    <text
      x="100" y="96"
      textAnchor="middle"
      fontFamily="'Georgia', serif"
      fontStyle="italic"
      fontWeight="400"
      fontSize="15"
      fill="url(#g12)"
      opacity="0.65"
    >
      Dr.
    </text>

    {/* THOMAS MILLER */}
    <text
      x="100" y="118"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="19"
      fill="url(#g12)"
      letterSpacing="2.5"
    >
      THOMAS MILLER
    </text>

    {/* Thin rule */}
    <line x1="50" y1="129" x2="150" y2="129" stroke="url(#g12)" strokeWidth="0.6" opacity="0.2" />

    {/* SERVICES */}
    <text
      x="100" y="144"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="10"
      fill="#8E8E93"
      letterSpacing="6"
    >
      SERVICES
    </text>

    {/* Tagline */}
    <text
      x="100" y="168"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      AI · BLOCKCHAIN · HEALTHCARE · DATA
    </text>
  </svg>
);

// ─── LOGO 13: Cap + Single Node ──────────────────────────────────────
// Tiny mortarboard icon with one blockchain dot, ultra minimal
const Logo13 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="g13" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5AC8FA" />
      </linearGradient>
      <filter id="glow13">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Small centered mortarboard */}
    <polygon points="100,20 135,35 100,50 65,35" fill="url(#g13)" />
    <rect x="75" y="42" width="50" height="10" rx="1.5" fill="url(#g13)" opacity="0.18" />

    {/* Tassel — short, simple */}
    <line x1="125" y1="35" x2="132" y2="48" stroke="#FFD60A" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="132" cy="50" r="3" fill="#FFD60A" />

    {/* Single blockchain node glow at tassel tip */}
    <circle cx="132" cy="50" r="6" fill="#FFD60A" opacity="0.15" filter="url(#glow13)" />

    {/* Dr. */}
    <text
      x="100" y="80"
      textAnchor="middle"
      fontFamily="'Georgia', serif"
      fontStyle="italic"
      fontWeight="400"
      fontSize="16"
      fill="url(#g13)"
      opacity="0.6"
    >
      Dr.
    </text>

    {/* THOMAS MILLER — larger, bolder */}
    <text
      x="100" y="108"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="800"
      fontSize="22"
      fill="url(#g13)"
      letterSpacing="2"
    >
      THOMAS MILLER
    </text>

    {/* Line */}
    <line x1="40" y1="120" x2="160" y2="120" stroke="url(#g13)" strokeWidth="0.6" opacity="0.2" />

    {/* SERVICES */}
    <text
      x="100" y="137"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="11"
      fill="#8E8E93"
      letterSpacing="6"
    >
      SERVICES
    </text>

    {/* Tagline */}
    <text
      x="100" y="162"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      AI · BLOCKCHAIN · HEALTHCARE · DATA
    </text>
  </svg>
);

// ─── LOGO 14: Rounded Cap Mark ───────────────────────────────────────
// Rounded rectangle badge with small cap icon, compact
const Logo14 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="g14" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="60%" stopColor="#5856D6" />
        <stop offset="100%" stopColor="#007AFF" />
      </linearGradient>
    </defs>

    {/* Rounded rectangle border */}
    <rect
      x="20" y="15"
      width="160" height="140"
      rx="20"
      fill="url(#g14)"
      opacity="0.05"
    />
    <rect
      x="20" y="15"
      width="160" height="140"
      rx="20"
      fill="none"
      stroke="url(#g14)"
      strokeWidth="1.5"
      opacity="0.4"
    />

    {/* Small cap icon centered at top */}
    <polygon points="100,28 130,40 100,52 70,40" fill="url(#g14)" opacity="0.8" />
    <rect x="78" y="46" width="44" height="8" rx="1.5" fill="url(#g14)" opacity="0.15" />

    {/* Tiny tassel */}
    <line x1="122" y1="40" x2="128" y2="50" stroke="#FFD60A" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="128" cy="52" r="2.5" fill="#FFD60A" />

    {/* Dr. */}
    <text
      x="100" y="76"
      textAnchor="middle"
      fontFamily="'Georgia', serif"
      fontStyle="italic"
      fontWeight="400"
      fontSize="13"
      fill="url(#g14)"
      opacity="0.6"
    >
      Dr.
    </text>

    {/* THOMAS MILLER */}
    <text
      x="100" y="98"
      textAnchor="middle"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="700"
      fontSize="18"
      fill="url(#g14)"
      letterSpacing="2"
    >
      THOMAS MILLER
    </text>

    {/* Thin line */}
    <line x1="45" y1="108" x2="155" y2="108" stroke="url(#g14)" strokeWidth="0.5" opacity="0.2" />

    {/* SERVICES */}
    <text
      x="100" y="124"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="9"
      fill="#8E8E93"
      letterSpacing="5"
    >
      SERVICES
    </text>

    {/* Tagline below box */}
    <text
      x="100" y="175"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="7"
      fill="#6E6E73"
      letterSpacing="2"
    >
      AI · BLOCKCHAIN · HEALTHCARE · DATA
    </text>
  </svg>
);

// ─── LOGO 15: Cap Wordmark ───────────────────────────────────────────
// Horizontal wordmark with tiny cap replacing the dot on "Dr."
const Logo15 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 240 160" width={size * 1.2} height={size * 0.8} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="g15" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5AC8FA" />
      </linearGradient>
      <linearGradient id="g15v" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#007AFF" />
        <stop offset="100%" stopColor="#5856D6" />
      </linearGradient>
    </defs>

    {/* Tiny mortarboard as the "dot" on Dr — positioned above the period */}
    <polygon points="54,24 66,30 54,36 42,30" fill="url(#g15v)" />
    <rect x="46" y="33" width="16" height="4" rx="1" fill="url(#g15v)" opacity="0.2" />
    {/* Micro tassel */}
    <line x1="62" y1="30" x2="66" y2="36" stroke="#FFD60A" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="66" cy="37" r="1.8" fill="#FFD60A" />

    {/* "Dr" text — italic serif, positioned so cap sits above */}
    <text
      x="10" y="60"
      fontFamily="'Georgia', serif"
      fontStyle="italic"
      fontWeight="400"
      fontSize="28"
      fill="url(#g15v)"
      opacity="0.7"
    >
      Dr.
    </text>

    {/* THOMAS MILLER — big bold */}
    <text
      x="78" y="60"
      fontFamily="'SF Pro Display', 'Inter', system-ui, sans-serif"
      fontWeight="800"
      fontSize="28"
      fill="url(#g15)"
      letterSpacing="1.5"
    >
      THOMAS MILLER
    </text>

    {/* Thin horizontal line */}
    <line x1="10" y1="72" x2="230" y2="72" stroke="url(#g15)" strokeWidth="0.8" opacity="0.2" />

    {/* SERVICES */}
    <text
      x="120" y="90"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="400"
      fontSize="12"
      fill="#8E8E93"
      letterSpacing="8"
    >
      SERVICES
    </text>

    {/* Tagline */}
    <text
      x="120" y="118"
      textAnchor="middle"
      fontFamily="'SF Pro Text', 'Inter', system-ui, sans-serif"
      fontWeight="300"
      fontSize="8"
      fill="#6E6E73"
      letterSpacing="2.5"
    >
      AI · BLOCKCHAIN · HEALTHCARE · DATA
    </text>
  </svg>
);

// ─── LOGO 16: Triangle Cap ───────────────────────────────────────────
// Just a triangle + rectangle. That's it.
const Logo16 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 180" width={size} height={size * 0.9} xmlns="http://www.w3.org/2000/svg">
    {/* Triangle = cap top */}
    <polygon points="100,16 140,40 60,40" fill="#007AFF" />
    {/* Rectangle = cap band */}
    <rect x="68" y="40" width="64" height="10" fill="#007AFF" opacity="0.25" />
    {/* Tassel = one line + one dot */}
    <line x1="132" y1="36" x2="140" y2="50" stroke="#FFD60A" strokeWidth="2" strokeLinecap="round" />
    <circle cx="140" cy="52" r="3" fill="#FFD60A" />

    <text x="100" y="76" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="98" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="45" y1="108" x2="155" y2="108" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="124" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="100" y="148" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 17: Circle + Cap ───────────────────────────────────────────
// Cap inside a plain circle
const Logo17 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    {/* Circle */}
    <circle cx="100" cy="42" r="32" fill="none" stroke="#007AFF" strokeWidth="2" />
    {/* Cap inside circle — triangle + bar */}
    <polygon points="100,22 124,36 76,36" fill="#007AFF" />
    <rect x="82" y="36" width="36" height="7" fill="#007AFF" opacity="0.2" />
    {/* Tassel */}
    <line x1="118" y1="33" x2="124" y2="44" stroke="#FFD60A" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="124" cy="46" r="2.5" fill="#FFD60A" />

    <text x="100" y="92" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="114" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="45" y1="124" x2="155" y2="124" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="140" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="100" y="164" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 18: Square Badge ───────────────────────────────────────────
// Cap on top of a square, everything aligned
const Logo18 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    {/* Square */}
    <rect x="58" y="18" width="84" height="60" rx="4" fill="none" stroke="#007AFF" strokeWidth="2" />
    {/* Cap — triangle + bar inside top of square */}
    <polygon points="100,24 128,38 72,38" fill="#007AFF" />
    <rect x="78" y="38" width="44" height="7" fill="#007AFF" opacity="0.2" />
    {/* Tassel */}
    <line x1="122" y1="35" x2="128" y2="46" stroke="#FFD60A" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="128" cy="48" r="2.5" fill="#FFD60A" />
    {/* "D" inside square bottom half */}
    <text x="100" y="68" textAnchor="middle" fontFamily="'Georgia', serif" fontWeight="700" fontSize="18" fill="#007AFF" opacity="0.5">D</text>

    <text x="100" y="100" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="122" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="45" y1="132" x2="155" y2="132" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="148" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="100" y="172" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 19: Diamond Cap ────────────────────────────────────────────
// Cap is just a diamond (rotated square) — single shape
const Logo19 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 180" width={size} height={size * 0.9} xmlns="http://www.w3.org/2000/svg">
    {/* Diamond = the whole cap, one shape */}
    <polygon points="100,10 145,35 100,60 55,35" fill="#007AFF" />
    {/* Tassel */}
    <line x1="136" y1="30" x2="148" y2="48" stroke="#FFD60A" strokeWidth="2" strokeLinecap="round" />
    <circle cx="148" cy="50" r="3.5" fill="#FFD60A" />

    <text x="100" y="82" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="15" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="104" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="45" y1="114" x2="155" y2="114" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="130" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="100" y="154" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 20: Two Lines Only ─────────────────────────────────────────
// No shape at all — just two crossed lines forming an abstract cap, maximum minimal
const Logo20 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 180" width={size} height={size * 0.9} xmlns="http://www.w3.org/2000/svg">
    {/* Two lines = abstract mortarboard */}
    <line x1="55" y1="40" x2="100" y2="18" stroke="#007AFF" strokeWidth="3" strokeLinecap="round" />
    <line x1="100" y1="18" x2="145" y2="40" stroke="#007AFF" strokeWidth="3" strokeLinecap="round" />
    {/* Horizontal brim */}
    <line x1="60" y1="40" x2="140" y2="40" stroke="#007AFF" strokeWidth="1.5" opacity="0.3" strokeLinecap="round" />
    {/* Tassel — single stroke */}
    <line x1="138" y1="36" x2="148" y2="52" stroke="#FFD60A" strokeWidth="2" strokeLinecap="round" />
    <circle cx="148" cy="54" r="3" fill="#FFD60A" />

    <text x="100" y="76" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="15" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="98" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="45" y1="108" x2="155" y2="108" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="124" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="100" y="148" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 21: Solid Block ────────────────────────────────────────────
// Big solid blue rectangle with white cap and white text punched out
const Logo21 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 220 200" width={size * 1.1} height={size} xmlns="http://www.w3.org/2000/svg">
    {/* Solid blue block */}
    <rect x="10" y="10" width="200" height="130" rx="12" fill="#007AFF" />

    {/* White cap — big, bold, centered */}
    <polygon points="110,28 155,50 65,50" fill="white" />
    <rect x="75" y="50" width="70" height="12" rx="2" fill="white" opacity="0.3" />

    {/* Gold tassel — thick */}
    <line x1="145" y1="44" x2="158" y2="62" stroke="#FFD60A" strokeWidth="3" strokeLinecap="round" />
    <circle cx="158" cy="65" r="5" fill="#FFD60A" />

    {/* White text inside block */}
    <text x="110" y="88" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="white" opacity="0.7">Dr.</text>
    <text x="110" y="112" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="22" fill="white" letterSpacing="3">THOMAS MILLER</text>
    <text x="110" y="130" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="white" opacity="0.6" letterSpacing="5">SERVICES</text>

    {/* Tagline below block */}
    <text x="110" y="165" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 22: Inverted Circle ────────────────────────────────────────
// Big filled circle with white knockout text and cap
const Logo22 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 210" width={size} height={size * 1.05} xmlns="http://www.w3.org/2000/svg">
    {/* Big solid circle */}
    <circle cx="100" cy="90" r="82" fill="#007AFF" />

    {/* White cap — chunky */}
    <polygon points="100,28 142,48 58,48" fill="white" />
    <rect x="68" y="48" width="64" height="11" rx="2" fill="white" opacity="0.3" />

    {/* Gold tassel */}
    <line x1="134" y1="43" x2="146" y2="58" stroke="#FFD60A" strokeWidth="3" strokeLinecap="round" />
    <circle cx="146" cy="61" r="4.5" fill="#FFD60A" />

    {/* White text knockout */}
    <text x="100" y="82" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="15" fill="white" opacity="0.75">Dr.</text>
    <text x="100" y="108" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="20" fill="white" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="118" x2="160" y2="118" stroke="white" strokeWidth="0.8" opacity="0.25" />
    <text x="100" y="136" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="white" opacity="0.6" letterSpacing="5">SERVICES</text>

    {/* Tagline below */}
    <text x="100" y="195" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 23: Split Block ────────────────────────────────────────────
// Two-tone split — dark left, blue right, cap spans both
const Logo23 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 220 200" width={size * 1.1} height={size} xmlns="http://www.w3.org/2000/svg">
    {/* Left block — dark */}
    <rect x="10" y="10" width="100" height="120" rx="12" ry="12" fill="#1A1A2E" />
    {/* Right block — blue */}
    <rect x="110" y="10" width="100" height="120" rx="12" ry="12" fill="#007AFF" />
    {/* Overlap cover for inner corners */}
    <rect x="98" y="10" width="24" height="120" fill="#1A1A2E" />
    <rect x="110" y="10" width="12" height="120" fill="#007AFF" />

    {/* Big bold cap spanning both halves */}
    <polygon points="110,22 160,46 60,46" fill="white" />
    <rect x="72" y="46" width="76" height="10" rx="2" fill="white" opacity="0.25" />

    {/* Gold tassel */}
    <line x1="150" y1="40" x2="164" y2="56" stroke="#FFD60A" strokeWidth="3" strokeLinecap="round" />
    <circle cx="164" cy="59" r="5" fill="#FFD60A" />

    {/* White text */}
    <text x="110" y="78" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="white" opacity="0.7">Dr.</text>
    <text x="110" y="102" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="21" fill="white" letterSpacing="3">THOMAS MILLER</text>
    <text x="110" y="122" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="white" opacity="0.5" letterSpacing="5">SERVICES</text>

    {/* Tagline */}
    <text x="110" y="158" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 24: Thick Stripe ───────────────────────────────────────────
// Bold horizontal stripe behind the text, cap sits above
const Logo24 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 220 180" width={size * 1.1} height={size * 0.9} xmlns="http://www.w3.org/2000/svg">
    {/* Big solid cap — heavy fill */}
    <polygon points="110,8 168,38 52,38" fill="#007AFF" />
    <rect x="65" y="38" width="90" height="14" rx="3" fill="#007AFF" opacity="0.3" />

    {/* Gold tassel — thick and visible */}
    <line x1="156" y1="32" x2="172" y2="52" stroke="#FFD60A" strokeWidth="3.5" strokeLinecap="round" />
    <circle cx="172" cy="55" r="6" fill="#FFD60A" />

    {/* Bold blue stripe behind name */}
    <rect x="0" y="78" width="220" height="44" fill="#007AFF" />

    {/* Dr. above stripe */}
    <text x="110" y="72" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="16" fill="#007AFF" opacity="0.7">Dr.</text>

    {/* White text on stripe */}
    <text x="110" y="106" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="24" fill="white" letterSpacing="3">THOMAS MILLER</text>

    {/* SERVICES below stripe */}
    <text x="110" y="140" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500" fontSize="12" fill="#007AFF" letterSpacing="6">SERVICES</text>

    {/* Tagline */}
    <text x="110" y="164" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 25: Dark Shield ────────────────────────────────────────────
// Dark filled shield with white cap and bright text
const Logo25 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    {/* Big solid dark shield */}
    <path
      d="M100,10 L175,40 L175,110 Q175,165 100,190 Q25,165 25,110 L25,40 Z"
      fill="#1A1A2E"
    />
    {/* Blue edge glow */}
    <path
      d="M100,10 L175,40 L175,110 Q175,165 100,190 Q25,165 25,110 L25,40 Z"
      fill="none"
      stroke="#007AFF"
      strokeWidth="3"
    />

    {/* White cap — big and bold inside shield */}
    <polygon points="100,32 145,52 55,52" fill="white" />
    <rect x="66" y="52" width="68" height="10" rx="2" fill="white" opacity="0.25" />

    {/* Gold tassel */}
    <line x1="136" y1="47" x2="148" y2="62" stroke="#FFD60A" strokeWidth="3" strokeLinecap="round" />
    <circle cx="148" cy="65" r="4.5" fill="#FFD60A" />

    {/* Bright text inside shield */}
    <text x="100" y="84" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="15" fill="#007AFF" opacity="0.8">Dr.</text>
    <text x="100" y="110" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="19" fill="white" letterSpacing="2">THOMAS MILLER</text>
    <line x1="45" y1="120" x2="155" y2="120" stroke="#007AFF" strokeWidth="1" opacity="0.3" />
    <text x="100" y="140" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="#007AFF" letterSpacing="5">SERVICES</text>

    {/* Tagline inside shield bottom */}
    <text x="100" y="164" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="6.5" fill="rgba(255,255,255,0.4)" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 26: Lightning Rod of Asclepius ─────────────────────────────
// Lightning bolt replaces the rod, snake wraps around it
const Logo26 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    {/* Lightning bolt — the rod */}
    <path
      d="M108,12 L88,70 L104,70 L84,130"
      fill="none"
      stroke="#007AFF"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Snake wrapping around the bolt — simplified S-curves */}
    <path
      d="M94,30 Q120,40 106,55 Q88,68 112,82 Q130,92 104,108 Q82,118 100,128"
      fill="none"
      stroke="#FFD60A"
      strokeWidth="3"
      strokeLinecap="round"
    />
    {/* Snake head */}
    <circle cx="100" cy="128" r="4" fill="#FFD60A" />
    <circle cx="97" cy="126" r="1" fill="#1A1A2E" />

    {/* Dr. */}
    <text x="100" y="154" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="#007AFF" opacity="0.6">Dr.</text>
    {/* THOMAS MILLER */}
    <text x="100" y="176" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="186" x2="160" y2="186" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="200" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
  </svg>
);

// ─── LOGO 27: Lightning Cross ────────────────────────────────────────
// Medical cross made of lightning bolt shapes
const Logo27 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 210" width={size} height={size * 1.05} xmlns="http://www.w3.org/2000/svg">
    {/* Solid blue circle background */}
    <circle cx="100" cy="75" r="68" fill="#007AFF" />

    {/* Medical cross — white, thick */}
    <rect x="85" y="28" width="30" height="94" rx="4" fill="white" />
    <rect x="53" y="60" width="94" height="30" rx="4" fill="white" />

    {/* Lightning bolt in center of cross — blue on white */}
    <path
      d="M106,42 L94,68 L106,68 L92,98"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Dr. */}
    <text x="100" y="162" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="184" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="194" x2="160" y2="194" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="208" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
  </svg>
);

// ─── LOGO 28: Lightning Shield ───────────────────────────────────────
// Dark shield with a fat lightning bolt through the middle
const Logo28 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 220" width={size} height={size * 1.1} xmlns="http://www.w3.org/2000/svg">
    {/* Dark shield */}
    <path
      d="M100,10 L170,38 L170,100 Q170,155 100,180 Q30,155 30,100 L30,38 Z"
      fill="#1A1A2E"
    />
    <path
      d="M100,10 L170,38 L170,100 Q170,155 100,180 Q30,155 30,100 L30,38 Z"
      fill="none"
      stroke="#007AFF"
      strokeWidth="3"
    />

    {/* Big bold lightning bolt — filled, white */}
    <path
      d="M112,30 L88,80 L105,80 L82,145 L120,90 L102,90 Z"
      fill="white"
    />

    {/* Small caduceus wings at top of bolt */}
    <path d="M98,44 Q78,36 72,44" fill="none" stroke="#FFD60A" strokeWidth="2" strokeLinecap="round" />
    <path d="M114,44 Q134,36 140,44" fill="none" stroke="#FFD60A" strokeWidth="2" strokeLinecap="round" />

    {/* Dr. */}
    <text x="100" y="198" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="13" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="215" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="16" fill="#007AFF" letterSpacing="2">THOMAS MILLER SERVICES</text>
  </svg>
);

// ─── LOGO 29: Heartbeat Lightning ────────────────────────────────────
// ECG/heartbeat line that spikes into a lightning bolt
const Logo29 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 220 190" width={size * 1.1} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    {/* ECG heartbeat line — flat, spike into lightning, flat again */}
    <path
      d="M10,60 L60,60 L75,60 L85,30 L100,80 L115,10 L125,60 L135,60 L145,60 L210,60"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Small pulse glow at the peak */}
    <circle cx="115" cy="10" r="6" fill="#007AFF" opacity="0.15" />
    <circle cx="115" cy="10" r="3" fill="#007AFF" opacity="0.3" />

    {/* Gold accent dots at flat line ends */}
    <circle cx="10" cy="60" r="3" fill="#FFD60A" />
    <circle cx="210" cy="60" r="3" fill="#FFD60A" />

    {/* Dr. */}
    <text x="110" y="94" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="15" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="110" y="118" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="22" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="128" x2="180" y2="128" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="110" y="144" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="110" y="168" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 30: Lightning Stethoscope ──────────────────────────────────
// Stethoscope shape where the tube is a lightning bolt
const Logo30 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 210" width={size} height={size * 1.05} xmlns="http://www.w3.org/2000/svg">
    {/* Stethoscope earpieces — two small circles at top */}
    <circle cx="72" cy="18" r="5" fill="#007AFF" />
    <circle cx="128" cy="18" r="5" fill="#007AFF" />

    {/* Tubes going down from earpieces to junction */}
    <path d="M72,23 Q72,50 90,55" fill="none" stroke="#007AFF" strokeWidth="3" strokeLinecap="round" />
    <path d="M128,23 Q128,50 110,55" fill="none" stroke="#007AFF" strokeWidth="3" strokeLinecap="round" />

    {/* Junction */}
    <circle cx="100" cy="58" r="4" fill="#007AFF" />

    {/* Lightning bolt as the tube going down */}
    <path
      d="M100,62 L92,85 L105,85 L88,120"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Chest piece — big gold circle at bottom of bolt */}
    <circle cx="88" cy="124" r="14" fill="none" stroke="#FFD60A" strokeWidth="3" />
    <circle cx="88" cy="124" r="6" fill="#FFD60A" />

    {/* Dr. */}
    <text x="100" y="160" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="14" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="100" y="182" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="800" fontSize="20" fill="#007AFF" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="192" x2="160" y2="192" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="100" y="206" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="10" fill="#8E8E93" letterSpacing="6">SERVICES</text>
  </svg>
);

// ─── LOGO 31: Neural Heartbeat ───────────────────────────────────────
// ECG spike with neural network nodes branching off the peak
const Logo31 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 240 190" width={size * 1.2} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="glow31">
        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
        <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* ECG line — bold */}
    <path
      d="M5,58 L65,58 L80,58 L92,28 L108,78 L120,8 L132,58 L145,58 L235,58"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Neural nodes branching off the lightning peak */}
    {/* Main peak node */}
    <circle cx="120" cy="8" r="6" fill="#007AFF" filter="url(#glow31)" />

    {/* Branch left */}
    <line x1="120" y1="8" x2="88" y2="4" stroke="#5AC8FA" strokeWidth="2" opacity="0.5" />
    <circle cx="86" cy="4" r="4" fill="#5AC8FA" filter="url(#glow31)" />
    <line x1="86" y1="4" x2="62" y2="12" stroke="#5AC8FA" strokeWidth="1.5" opacity="0.3" />
    <circle cx="60" cy="12" r="3" fill="#5AC8FA" opacity="0.5" />

    {/* Branch right */}
    <line x1="120" y1="8" x2="152" y2="4" stroke="#5856D6" strokeWidth="2" opacity="0.5" />
    <circle cx="154" cy="4" r="4" fill="#5856D6" filter="url(#glow31)" />
    <line x1="154" y1="4" x2="178" y2="12" stroke="#5856D6" strokeWidth="1.5" opacity="0.3" />
    <circle cx="180" cy="12" r="3" fill="#5856D6" opacity="0.5" />

    {/* Branch up */}
    <line x1="120" y1="8" x2="120" y2="-12" stroke="#007AFF" strokeWidth="1.5" opacity="0.3" />

    {/* Gold accent dots at line ends */}
    <circle cx="5" cy="58" r="4" fill="#FFD60A" />
    <circle cx="235" cy="58" r="4" fill="#FFD60A" />

    {/* Dr. */}
    <text x="120" y="90" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="16" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="120" y="116" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="24" fill="#007AFF" letterSpacing="3">THOMAS MILLER</text>
    <line x1="35" y1="126" x2="205" y2="126" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="120" y="144" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="120" y="170" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7.5" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 32: Pulse on Dark Bar ──────────────────────────────────────
// Bold dark rectangle with glowing heartbeat-lightning across it
const Logo32 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 240 190" width={size * 1.2} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="glow32">
        <feGaussianBlur stdDeviation="4" result="coloredBlur" />
        <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* Dark bar */}
    <rect x="5" y="10" width="230" height="68" rx="10" fill="#0A0A1A" />
    {/* Blue edge */}
    <rect x="5" y="10" width="230" height="68" rx="10" fill="none" stroke="#007AFF" strokeWidth="2" opacity="0.4" />

    {/* Glowing ECG line on bar */}
    <path
      d="M20,46 L70,46 L84,46 L94,24 L108,62 L120,14 L132,46 L145,46 L220,46"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#glow32)"
    />

    {/* Bright glow at peak */}
    <circle cx="120" cy="14" r="5" fill="#5AC8FA" filter="url(#glow32)" />

    {/* AI brain icon — small, at right end of bar */}
    <circle cx="206" cy="46" r="10" fill="none" stroke="#5AC8FA" strokeWidth="1.5" opacity="0.5" />
    <path d="M200,42 Q206,36 212,42" fill="none" stroke="#5AC8FA" strokeWidth="1" opacity="0.4" />
    <path d="M200,50 Q206,56 212,50" fill="none" stroke="#5AC8FA" strokeWidth="1" opacity="0.4" />
    <circle cx="206" cy="46" r="2" fill="#5AC8FA" opacity="0.6" />

    {/* Dr. */}
    <text x="120" y="102" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="16" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="120" y="128" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="24" fill="#007AFF" letterSpacing="3">THOMAS MILLER</text>
    <line x1="35" y1="138" x2="205" y2="138" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="120" y="156" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="120" y="180" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7.5" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 33: Double Pulse ───────────────────────────────────────────
// Two heartbeat spikes — one for medicine, one for AI — merging
const Logo33 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 240 190" width={size * 1.2} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="glow33">
        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
        <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* First pulse — blue (healthcare) */}
    <path
      d="M5,50 L50,50 L60,50 L70,22 L82,68 L92,8 L102,50 L115,50"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#glow33)"
    />

    {/* Second pulse — purple (AI) */}
    <path
      d="M115,50 L128,50 L138,28 L148,64 L158,12 L168,50 L180,50 L235,50"
      fill="none"
      stroke="#5856D6"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#glow33)"
    />

    {/* Merge point — bright node where the two meet */}
    <circle cx="115" cy="50" r="6" fill="white" />
    <circle cx="115" cy="50" r="4" fill="#007AFF" filter="url(#glow33)" />

    {/* Peak nodes */}
    <circle cx="92" cy="8" r="4" fill="#007AFF" filter="url(#glow33)" />
    <circle cx="158" cy="12" r="4" fill="#5856D6" filter="url(#glow33)" />

    {/* Labels above peaks */}
    <text x="92" y="-2" textAnchor="middle" fontFamily="'Inter', sans-serif" fontWeight="700" fontSize="7" fill="#007AFF" opacity="0.6">MED</text>
    <text x="158" y="2" textAnchor="middle" fontFamily="'Inter', sans-serif" fontWeight="700" fontSize="7" fill="#5856D6" opacity="0.6">AI</text>

    {/* End dots */}
    <circle cx="5" cy="50" r="3.5" fill="#FFD60A" />
    <circle cx="235" cy="50" r="3.5" fill="#FFD60A" />

    {/* Dr. */}
    <text x="120" y="84" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="16" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="120" y="110" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="24" fill="#007AFF" letterSpacing="3">THOMAS MILLER</text>
    <line x1="35" y1="120" x2="205" y2="120" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="120" y="138" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="120" y="164" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7.5" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 34: Pulse Circuit Board ────────────────────────────────────
// Heartbeat with circuit-board traces extending from the baseline
const Logo34 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 240 195" width={size * 1.2} height={size * 0.975} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="glow34">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* Circuit traces dropping down from the ECG baseline */}
    <line x1="40" y1="58" x2="40" y2="75" stroke="#007AFF" strokeWidth="1.5" opacity="0.25" />
    <circle cx="40" cy="77" r="2.5" fill="#007AFF" opacity="0.3" />
    <line x1="65" y1="58" x2="65" y2="70" stroke="#5856D6" strokeWidth="1.5" opacity="0.25" />
    <circle cx="65" cy="72" r="2.5" fill="#5856D6" opacity="0.3" />
    <line x1="150" y1="58" x2="150" y2="70" stroke="#5856D6" strokeWidth="1.5" opacity="0.25" />
    <circle cx="150" cy="72" r="2.5" fill="#5856D6" opacity="0.3" />
    <line x1="180" y1="58" x2="180" y2="78" stroke="#007AFF" strokeWidth="1.5" opacity="0.25" />
    <circle cx="180" cy="80" r="2.5" fill="#007AFF" opacity="0.3" />
    <line x1="205" y1="58" x2="205" y2="68" stroke="#5AC8FA" strokeWidth="1.5" opacity="0.25" />
    <circle cx="205" cy="70" r="2.5" fill="#5AC8FA" opacity="0.3" />

    {/* Bold ECG line */}
    <path
      d="M5,58 L70,58 L84,58 L94,28 L110,78 L122,6 L134,58 L148,58 L235,58"
      fill="none"
      stroke="#007AFF"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Peak glow */}
    <circle cx="122" cy="6" r="7" fill="#007AFF" opacity="0.2" filter="url(#glow34)" />
    <circle cx="122" cy="6" r="4" fill="#5AC8FA" filter="url(#glow34)" />

    {/* Gold endpoints */}
    <circle cx="5" cy="58" r="4" fill="#FFD60A" />
    <circle cx="235" cy="58" r="4" fill="#FFD60A" />

    {/* Dr. */}
    <text x="120" y="102" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="16" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="120" y="128" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="24" fill="#007AFF" letterSpacing="3">THOMAS MILLER</text>
    <line x1="35" y1="138" x2="205" y2="138" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="120" y="156" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="120" y="182" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7.5" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 35: Pulse on Blue Block ────────────────────────────────────
// Solid blue block with white heartbeat-lightning punched through
const Logo35 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 240 190" width={size * 1.2} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    {/* Big solid blue block */}
    <rect x="5" y="5" width="230" height="80" rx="12" fill="#007AFF" />

    {/* White ECG line punched through the block */}
    <path
      d="M20,48 L70,48 L84,48 L94,22 L110,68 L122,10 L134,48 L148,48 L220,48"
      fill="none"
      stroke="white"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Gold peak dot */}
    <circle cx="122" cy="10" r="5" fill="#FFD60A" />

    {/* Gold endpoints inside block */}
    <circle cx="20" cy="48" r="4" fill="#FFD60A" opacity="0.7" />
    <circle cx="220" cy="48" r="4" fill="#FFD60A" opacity="0.7" />

    {/* Dr. */}
    <text x="120" y="110" textAnchor="middle" fontFamily="'Georgia', serif" fontStyle="italic" fontSize="16" fill="#007AFF" opacity="0.6">Dr.</text>
    <text x="120" y="136" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="900" fontSize="24" fill="#007AFF" letterSpacing="3">THOMAS MILLER</text>
    <line x1="35" y1="146" x2="205" y2="146" stroke="#007AFF" strokeWidth="0.6" opacity="0.2" />
    <text x="120" y="164" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="11" fill="#8E8E93" letterSpacing="6">SERVICES</text>
    <text x="120" y="184" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="300" fontSize="7.5" fill="#6E6E73" letterSpacing="2">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 36: Network Topology ───────────────────────────────────────
// Simple nodes and lines — like a network diagram from a CS textbook
const Logo36 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 190" width={size} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    {/* Network nodes — plain circles */}
    <circle cx="100" cy="20" r="8" fill="none" stroke="#333" strokeWidth="2" />
    <circle cx="50" cy="55" r="8" fill="none" stroke="#333" strokeWidth="2" />
    <circle cx="150" cy="55" r="8" fill="none" stroke="#333" strokeWidth="2" />
    <circle cx="75" cy="90" r="8" fill="#333" />
    <circle cx="125" cy="90" r="8" fill="#333" />

    {/* Edges — plain lines */}
    <line x1="100" y1="28" x2="50" y2="47" stroke="#333" strokeWidth="1.5" />
    <line x1="100" y1="28" x2="150" y2="47" stroke="#333" strokeWidth="1.5" />
    <line x1="50" y1="63" x2="75" y2="82" stroke="#333" strokeWidth="1.5" />
    <line x1="150" y1="63" x2="125" y2="82" stroke="#333" strokeWidth="1.5" />
    <line x1="75" y1="90" x2="125" y2="90" stroke="#333" strokeWidth="1.5" />
    <line x1="50" y1="55" x2="150" y2="55" stroke="#333" strokeWidth="1" opacity="0.3" strokeDasharray="4 3" />

    {/* Text — monospace / plain */}
    <text x="100" y="118" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="12" fill="#555">Dr.</text>
    <text x="100" y="138" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#222" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="147" x2="160" y2="147" stroke="#333" strokeWidth="0.5" opacity="0.3" />
    <text x="100" y="162" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="9" fill="#888" letterSpacing="4">SERVICES</text>
    <text x="100" y="182" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="7" fill="#999" letterSpacing="1">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 37: Terminal Prompt ────────────────────────────────────────
// Looks like a terminal window with a command prompt
const Logo37 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 220 185" width={size * 1.1} height={size * 0.925} xmlns="http://www.w3.org/2000/svg">
    {/* Terminal window */}
    <rect x="10" y="8" width="200" height="100" rx="6" fill="#1E1E1E" />
    {/* Title bar */}
    <rect x="10" y="8" width="200" height="20" rx="6" fill="#2D2D2D" />
    <rect x="10" y="22" width="200" height="6" fill="#2D2D2D" />
    {/* Traffic lights */}
    <circle cx="24" cy="18" r="4" fill="#FF5F57" />
    <circle cx="36" cy="18" r="4" fill="#FEBC2E" />
    <circle cx="48" cy="18" r="4" fill="#28C840" />

    {/* Terminal text */}
    <text x="22" y="48" fontFamily="'Courier New', 'SF Mono', monospace" fontSize="10" fill="#28C840">$ whoami</text>
    <text x="22" y="63" fontFamily="'Courier New', 'SF Mono', monospace" fontSize="10" fill="#CCC">Dr. Thomas Miller</text>
    <text x="22" y="78" fontFamily="'Courier New', 'SF Mono', monospace" fontSize="10" fill="#28C840">$ cat services.txt</text>
    <text x="22" y="93" fontFamily="'Courier New', 'SF Mono', monospace" fontSize="10" fill="#CCC">AI, Blockchain, Healthcare, Data</text>

    {/* Name below */}
    <text x="110" y="128" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#222" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="137" x2="180" y2="137" stroke="#333" strokeWidth="0.5" opacity="0.3" />
    <text x="110" y="152" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="9" fill="#888" letterSpacing="4">SERVICES</text>
    <text x="110" y="175" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="7" fill="#999" letterSpacing="1">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 38: Binary Tree ────────────────────────────────────────────
// CS binary tree structure — simple squares connected by lines
const Logo38 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 190" width={size} height={size * 0.95} xmlns="http://www.w3.org/2000/svg">
    {/* Root node — square */}
    <rect x="90" y="10" width="20" height="20" fill="#333" />
    {/* Level 2 */}
    <rect x="48" y="48" width="16" height="16" fill="#333" />
    <rect x="136" y="48" width="16" height="16" fill="#333" />
    {/* Level 3 */}
    <rect x="28" y="82" width="12" height="12" fill="#555" />
    <rect x="68" y="82" width="12" height="12" fill="#555" />
    <rect x="120" y="82" width="12" height="12" fill="#555" />
    <rect x="160" y="82" width="12" height="12" fill="#555" />

    {/* Edges */}
    <line x1="100" y1="30" x2="56" y2="48" stroke="#333" strokeWidth="1.5" />
    <line x1="100" y1="30" x2="144" y2="48" stroke="#333" strokeWidth="1.5" />
    <line x1="56" y1="64" x2="34" y2="82" stroke="#555" strokeWidth="1" />
    <line x1="56" y1="64" x2="74" y2="82" stroke="#555" strokeWidth="1" />
    <line x1="144" y1="64" x2="126" y2="82" stroke="#555" strokeWidth="1" />
    <line x1="144" y1="64" x2="166" y2="82" stroke="#555" strokeWidth="1" />

    {/* Text */}
    <text x="100" y="114" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="12" fill="#555">Dr.</text>
    <text x="100" y="136" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#222" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="145" x2="160" y2="145" stroke="#333" strokeWidth="0.5" opacity="0.3" />
    <text x="100" y="160" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="9" fill="#888" letterSpacing="4">SERVICES</text>
    <text x="100" y="182" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="7" fill="#999" letterSpacing="1">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 39: Angle Brackets ─────────────────────────────────────────
// Simple < D / > code tag wrapping the initial
const Logo39 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 180" width={size} height={size * 0.9} xmlns="http://www.w3.org/2000/svg">
    {/* < bracket */}
    <text x="40" y="58" fontFamily="'Courier New', monospace" fontWeight="300" fontSize="48" fill="#CCC">&lt;</text>
    {/* D */}
    <text x="100" y="58" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="700" fontSize="48" fill="#333">D</text>
    {/* / > bracket */}
    <text x="124" y="58" fontFamily="'Courier New', monospace" fontWeight="300" fontSize="48" fill="#CCC">/&gt;</text>

    {/* Text */}
    <text x="100" y="88" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="11" fill="#888">Dr.</text>
    <text x="100" y="110" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#222" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="120" x2="160" y2="120" stroke="#333" strokeWidth="0.5" opacity="0.3" />
    <text x="100" y="135" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="9" fill="#888" letterSpacing="4">SERVICES</text>
    <text x="100" y="158" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="7" fill="#999" letterSpacing="1">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 40: Graph Paper ────────────────────────────────────────────
// Grid background with a simple node-link graph plotted on it
const Logo40 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 200 195" width={size} height={size * 0.975} xmlns="http://www.w3.org/2000/svg">
    {/* Grid lines — faint graph paper */}
    {[0,1,2,3,4,5,6].map(i => (
      <line key={`h${i}`} x1="20" y1={15 + i * 15} x2="180" y2={15 + i * 15} stroke="#DDD" strokeWidth="0.5" />
    ))}
    {[0,1,2,3,4,5,6,7,8,9,10].map(i => (
      <line key={`v${i}`} x1={20 + i * 16} y1="15" x2={20 + i * 16} y2="105" stroke="#DDD" strokeWidth="0.5" />
    ))}

    {/* Plotted graph — nodes and edges */}
    <circle cx="52" cy="75" r="6" fill="#333" />
    <circle cx="100" cy="30" r="8" fill="#333" />
    <circle cx="148" cy="60" r="6" fill="#333" />
    <circle cx="84" cy="90" r="5" fill="#555" />
    <circle cx="132" cy="90" r="5" fill="#555" />

    <line x1="52" y1="75" x2="100" y2="30" stroke="#333" strokeWidth="1.5" />
    <line x1="100" y1="30" x2="148" y2="60" stroke="#333" strokeWidth="1.5" />
    <line x1="52" y1="75" x2="84" y2="90" stroke="#555" strokeWidth="1" />
    <line x1="148" y1="60" x2="132" y2="90" stroke="#555" strokeWidth="1" />
    <line x1="84" y1="90" x2="132" y2="90" stroke="#555" strokeWidth="1" strokeDasharray="3 2" />

    {/* Text */}
    <text x="100" y="125" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="12" fill="#555">Dr.</text>
    <text x="100" y="146" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#222" letterSpacing="2">THOMAS MILLER</text>
    <line x1="40" y1="155" x2="160" y2="155" stroke="#333" strokeWidth="0.5" opacity="0.3" />
    <text x="100" y="170" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight="400" fontSize="9" fill="#888" letterSpacing="4">SERVICES</text>
    <text x="100" y="190" textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="400" fontSize="7" fill="#999" letterSpacing="1">AI · BLOCKCHAIN · HEALTHCARE · DATA</text>
  </svg>
);

// ─── LOGO 41: Current Bolt + DrThomasMillerServices (horizontal) ────────
// The actual current lightning bolt with text beside it
const Logo41 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 370 50" width={size * 1.85} height={size * 0.25} xmlns="http://www.w3.org/2000/svg">
    {/* Lightning bolt — exact same path as Logo.tsx, scaled up */}
    <g transform="translate(8, 5) scale(1.7)">
      <path d="M7 2v11h3v9l7-12h-4l4-8z" fill="#369eff" />
    </g>

    {/* DrThomasMillerServices — single line beside bolt */}
    <text
      x="48" y="32"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="600"
      fontSize="20"
      fill="white"
      letterSpacing="-0.02em"
    >
      DrThomasMillerServices
    </text>
  </svg>
);

// ─── LOGO 42: Bolt + Spaced Text ─────────────────────────────────────
// Same bolt but with "Dr Thomas Miller Services" properly spaced
const Logo42 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 390 50" width={size * 1.95} height={size * 0.25} xmlns="http://www.w3.org/2000/svg">
    {/* Lightning bolt */}
    <g transform="translate(8, 5) scale(1.7)">
      <path d="M7 2v11h3v9l7-12h-4l4-8z" fill="#369eff" />
    </g>

    {/* Dr Thomas Miller Services — spaced out */}
    <text
      x="48" y="32"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="600"
      fontSize="20"
      fill="white"
      letterSpacing="-0.02em"
    >
      Dr Thomas Miller Services
    </text>
  </svg>
);

// ─── LOGO 43: Bolt + Two-line Stack ──────────────────────────────────
// Bolt on the left, "Dr Thomas Miller" + "Services" stacked on the right
const Logo43 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 320 60" width={size * 1.6} height={size * 0.3} xmlns="http://www.w3.org/2000/svg">
    {/* Lightning bolt — bigger */}
    <g transform="translate(6, 4) scale(2.2)">
      <path d="M7 2v11h3v9l7-12h-4l4-8z" fill="#369eff" />
    </g>

    {/* Dr Thomas Miller — bold */}
    <text
      x="56" y="28"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="700"
      fontSize="20"
      fill="white"
      letterSpacing="-0.01em"
    >
      Dr Thomas Miller
    </text>
    {/* Services — lighter */}
    <text
      x="56" y="48"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="400"
      fontSize="13"
      fill="#8E8E93"
      letterSpacing="3"
    >
      SERVICES
    </text>
  </svg>
);

// ─── LOGO 44: Bolt in Circle + Text ──────────────────────────────────
// Bolt inside a circle badge, text beside it
const Logo44 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 340 56" width={size * 1.7} height={size * 0.28} xmlns="http://www.w3.org/2000/svg">
    {/* Circle behind bolt */}
    <circle cx="28" cy="28" r="26" fill="rgba(54, 158, 255, 0.12)" />
    <circle cx="28" cy="28" r="26" fill="none" stroke="#369eff" strokeWidth="1.5" opacity="0.4" />

    {/* Lightning bolt centered in circle */}
    <g transform="translate(13, 7) scale(1.8)">
      <path d="M7 2v11h3v9l7-12h-4l4-8z" fill="#369eff" />
    </g>

    {/* Dr Thomas Miller Services */}
    <text
      x="64" y="24"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="700"
      fontSize="19"
      fill="white"
      letterSpacing="-0.01em"
    >
      Dr Thomas Miller
    </text>
    <text
      x="64" y="44"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="400"
      fontSize="12"
      fill="#8E8E93"
      letterSpacing="3"
    >
      SERVICES
    </text>
  </svg>
);

// ─── LOGO 45: Bolt + Bold Joined Text ────────────────────────────────
// Bolt with "DrThomasMillerServices" in one bold slug, startup-style
const Logo45 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 400 56" width={size * 2} height={size * 0.28} xmlns="http://www.w3.org/2000/svg">
    {/* Lightning bolt — with glow */}
    <g transform="translate(8, 6) scale(1.9)" filter="url(#glow45)">
      <path d="M7 2v11h3v9l7-12h-4l4-8z" fill="#369eff" />
    </g>
    <defs>
      <filter id="glow45">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* "Dr" in blue */}
    <text
      x="50" y="34"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="800"
      fontSize="22"
      fill="#369eff"
    >
      Dr
    </text>
    {/* "ThomasMillerServices" in white */}
    <text
      x="80" y="34"
      fontFamily="'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
      fontWeight="600"
      fontSize="22"
      fill="white"
      letterSpacing="-0.02em"
    >
      ThomasMillerServices
    </text>
  </svg>
);

// ─── LOGO 46: Current Logo (Dark BG) ─────────────────────────────────
// The actual live logo — blue "Dr" + white text, for dark backgrounds
const Logo46 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 280 32" width={size * 1.4} height={size * 0.16} xmlns="http://www.w3.org/2000/svg">
    <text x="0" y="24" fontFamily="Geist, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif" fontSize="24" letterSpacing="-0.03em">
      <tspan fontWeight="800" fill="#369eff">Dr</tspan><tspan fontWeight="700" fill="#FFFFFF">TomMillerServices</tspan>
    </text>
  </svg>
);

// ─── LOGO 47: Current Logo (Light BG) ────────────────────────────────
// The actual live logo — darker blue "Dr" + dark text, for light backgrounds
const Logo47 = ({ size = 200 }: { size?: number }) => (
  <svg viewBox="0 0 280 32" width={size * 1.4} height={size * 0.16} xmlns="http://www.w3.org/2000/svg">
    <text x="0" y="24" fontFamily="Geist, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif" fontSize="24" letterSpacing="-0.03em">
      <tspan fontWeight="800" fill="#0051D0">Dr</tspan><tspan fontWeight="700" fill="#1A202C">TomMillerServices</tspan>
    </text>
  </svg>
);

// ─── Logo Card Component ─────────────────────────────────────────────
const LogoCard = ({
  title,
  subtitle,
  description,
  tags,
  children,
  index,
}: {
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
  children: React.ReactNode;
  index: number;
}) => {
  const { colorMode } = useColorMode();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      bg={colorMode === "dark" ? "rgba(20, 20, 25, 0.8)" : "white"}
      border="1px solid"
      borderColor={
        isHovered
          ? "rgba(0, 122, 255, 0.4)"
          : colorMode === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.08)"
      }
      borderRadius="16px"
      p={8}
      transition="all 0.3s ease"
      transform={isHovered ? "translateY(-4px)" : "none"}
      boxShadow={
        isHovered
          ? "0 20px 40px rgba(0, 122, 255, 0.15)"
          : "0 4px 20px rgba(0, 0, 0, 0.1)"
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      cursor="pointer"
      position="relative"
      overflow="hidden"
    >
      {/* Number badge */}
      <Box
        position="absolute"
        top={4}
        right={4}
        bg="rgba(0, 122, 255, 0.1)"
        borderRadius="full"
        w="32px"
        h="32px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" fontWeight="700" color="#007AFF">
          {index}
        </Text>
      </Box>

      <VStack spacing={5} align="center">
        {/* Logo */}
        <Box
          py={6}
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="transform 0.3s ease"
          transform={isHovered ? "scale(1.05)" : "scale(1)"}
        >
          {children}
        </Box>

        {/* Title */}
        <VStack spacing={1}>
          <Text
            fontSize="lg"
            fontWeight="700"
            color={colorMode === "dark" ? "white" : "#1D1D1F"}
            textAlign="center"
          >
            {title}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="400"
            color={colorMode === "dark" ? "rgba(255,255,255,0.5)" : "#6E6E73"}
            textAlign="center"
          >
            {subtitle}
          </Text>
        </VStack>

        {/* Tags */}
        <HStack spacing={2} flexWrap="wrap" justifyContent="center">
          {tags.map((tag) => (
            <Badge
              key={tag}
              bg="rgba(0, 122, 255, 0.1)"
              color="#5AC8FA"
              fontSize="xs"
              px={2}
              py={0.5}
              borderRadius="full"
              fontWeight="500"
              border="1px solid rgba(0, 122, 255, 0.2)"
            >
              {tag}
            </Badge>
          ))}
        </HStack>

        {/* Description */}
        <Text
          fontSize="sm"
          color={colorMode === "dark" ? "rgba(255,255,255,0.6)" : "#6E6E73"}
          textAlign="center"
          lineHeight="1.6"
        >
          {description}
        </Text>
      </VStack>
    </Box>
  );
};

// ─── Main Logos Page ─────────────────────────────────────────────────
const Logos = () => {
  const { colorMode } = useColorMode();

  return (
    <Box
      minH="100vh"
      bg={colorMode === "dark" ? "#08090a" : "#FAFAFA"}
    >
      <NavbarWithCallToAction />

      <Container maxW="1200px" pt={32} pb={20} px={6}>
        {/* Header */}
        <VStack spacing={4} mb={16} textAlign="center">
          <Badge
            bg="rgba(0, 122, 255, 0.1)"
            color="#007AFF"
            fontSize="xs"
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="600"
            letterSpacing="1.5px"
            border="1px solid rgba(0, 122, 255, 0.2)"
          >
            BRAND EXPLORATION
          </Badge>

          <Text
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            fontWeight="800"
            color={colorMode === "dark" ? "white" : "#1D1D1F"}
            lineHeight="1.1"
            letterSpacing="-0.03em"
          >
            Logo Concepts
          </Text>

          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={colorMode === "dark" ? "rgba(255,255,255,0.6)" : "#6E6E73"}
            maxW="600px"
            lineHeight="1.7"
          >
            Forty-five logo directions for Dr Thomas Miller Services.
            Blending blockchain, AI, data sovereignty, academic authority, and professional identity.
          </Text>
        </VStack>

        {/* Logo Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} mb={16}>
          <LogoCard
            title="TMS Hexagon Chain"
            subtitle="Blockchain-first identity"
            description="Hexagonal blockchain motif with bold TMS monogram. Chain-link connectors radiate out, symbolizing distributed networks and connected systems."
            tags={["Blockchain", "Web3", "Professional"]}
            index={1}
          >
            <Logo1 size={220} />
          </LogoCard>

          <LogoCard
            title="DTMS Neural Shield"
            subtitle="Dr Thomas Miller Services"
            description="Shield silhouette with embedded neural network. Projects trust and AI intelligence. The 'Dr' prefix elevates the brand's authority and expertise."
            tags={["AI", "Security", "Medical"]}
            index={2}
          >
            <Logo2 size={220} />
          </LogoCard>

          <LogoCard
            title="Sovereign Data Lock"
            subtitle="Data sovereignty focus"
            description="Circuit-board lock icon with radiating traces. Communicates data ownership, privacy-first architecture, and secure sovereign infrastructure."
            tags={["Privacy", "Sovereignty", "Encryption"]}
            index={3}
          >
            <Logo3 size={220} />
          </LogoCard>

          <LogoCard
            title="Medical Cross + Chain"
            subtitle="Healthcare meets blockchain"
            description="Medical cross with blockchain nodes at each tip. Perfect for healthcare SaaS positioning — bridging clinical systems with distributed ledger tech."
            tags={["Healthcare", "Blockchain", "Clinical"]}
            index={4}
          >
            <Logo4 size={220} />
          </LogoCard>

          <LogoCard
            title="TMS Circuit Monogram"
            subtitle="Clean modern identity"
            description="Overlapping T-M-S letterforms inside a circuit-node ring. Versatile and modern — works at any size from favicon to billboard. The gradient gives it energy."
            tags={["Modern", "Versatile", "Tech"]}
            index={5}
          >
            <Logo5 size={220} />
          </LogoCard>

          <LogoCard
            title="Graduation Cap + Chain"
            subtitle="Dr Thomas Miller Services"
            description="Classic mortarboard with a blockchain-node tassel. The chain links along the bottom tie academia to distributed systems. Scholarly meets sovereign tech."
            tags={["Academic", "Blockchain", "Doctorate"]}
            index={6}
          >
            <Logo6 size={220} />
          </LogoCard>

          <LogoCard
            title="Academic Crest"
            subtitle="University-seal style"
            description="Traditional circular crest with laurel wreath, graduation cap, and inner shield bearing 'D'. Curved text reads 'Dr Thomas Miller Services'. Prestige and authority."
            tags={["Crest", "Prestige", "Authority"]}
            index={7}
          >
            <Logo7 size={220} />
          </LogoCard>

          <LogoCard
            title="Mortarboard Monogram"
            subtitle="DTM lettermark with cap"
            description="Bold 'DTM' monogram with a graduation cap perched on top and gold tassel with fringe detail. Clean, stacked layout. Works great at small sizes."
            tags={["Monogram", "Minimalist", "Dr"]}
            index={8}
          >
            <Logo8 size={220} />
          </LogoCard>

          <LogoCard
            title="Scholar's Hex Badge"
            subtitle="Hexagon + mortarboard + nodes"
            description="Blockchain hexagon badge with graduation cap inside and glowing nodes at each vertex. 'DTMS' centered below the cap. Best of both worlds — academic and Web3."
            tags={["Hexagon", "Scholar", "Web3"]}
            index={9}
          >
            <Logo9 size={220} />
          </LogoCard>

          <LogoCard
            title="Professor's Mark"
            subtitle="Elegant stacked layout"
            description="Large mortarboard with a circuit-trace tassel that branches into glowing nodes. Italic 'Dr.' above bold 'THOMAS MILLER'. Premium feel, works across all media."
            tags={["Premium", "Elegant", "Professor"]}
            index={10}
          >
            <Logo10 size={220} />
          </LogoCard>

          <LogoCard
            title="Clean Cap Minimal"
            subtitle="Flat shape, no extras"
            description="Solid flat mortarboard with a simple rectangular band and single tassel dot. No glows, no circuits — just clean geometry and stacked typography."
            tags={["Minimal", "Flat", "Clean"]}
            index={11}
          >
            <Logo11 size={220} />
          </LogoCard>

          <LogoCard
            title="Outlined Cap Badge"
            subtitle="Stroke-only mortarboard"
            description="Cap drawn with outlines only — no fill. Feels lighter and more refined. Gold tassel as the single accent colour. Works beautifully on dark and light backgrounds."
            tags={["Outlined", "Light", "Refined"]}
            index={12}
          >
            <Logo12 size={220} />
          </LogoCard>

          <LogoCard
            title="Cap + Single Node"
            subtitle="Ultra minimal icon"
            description="Tiny mortarboard with just one glowing blockchain node at the tassel tip. Text is bigger and bolder — the name does the heavy lifting. Maximum simplicity."
            tags={["Ultra-min", "Bold Text", "Node"]}
            index={13}
          >
            <Logo13 size={220} />
          </LogoCard>

          <LogoCard
            title="Rounded Cap Mark"
            subtitle="Contained badge layout"
            description="Everything inside a rounded rectangle border. Small cap icon at the top, stacked text below. Compact and contained — perfect for app icons, business cards, letterheads."
            tags={["Contained", "App Icon", "Badge"]}
            index={14}
          >
            <Logo14 size={220} />
          </LogoCard>

          <LogoCard
            title="Cap Wordmark"
            subtitle="Horizontal layout"
            description="Horizontal wordmark where the mortarboard replaces the dot on 'Dr.' — a clever typographic detail. Wide format, ideal for headers, email signatures, and website navbars."
            tags={["Wordmark", "Horizontal", "Clever"]}
            index={15}
          >
            <Logo15 size={220} />
          </LogoCard>

          <LogoCard
            title="Triangle Cap"
            subtitle="3 shapes total"
            description="One triangle, one rectangle, one circle for the tassel. That's the entire icon. As simple as a mortarboard gets."
            tags={["3 Shapes", "Flat", "Simple"]}
            index={16}
          >
            <Logo16 size={220} />
          </LogoCard>

          <LogoCard
            title="Circle + Cap"
            subtitle="Cap inside a ring"
            description="Plain circle with a triangle-and-bar cap inside it. Clean containment — works as an app icon or favicon at any size."
            tags={["Circle", "Icon", "Contained"]}
            index={17}
          >
            <Logo17 size={220} />
          </LogoCard>

          <LogoCard
            title="Square Badge"
            subtitle="Rounded square frame"
            description="Cap sits inside a rounded square with a 'D' beneath it. Badge-style — perfect for app icons, social profiles, business cards."
            tags={["Square", "Badge", "App Icon"]}
            index={18}
          >
            <Logo18 size={220} />
          </LogoCard>

          <LogoCard
            title="Diamond Cap"
            subtitle="Single diamond shape"
            description="The entire cap is just one diamond polygon. One shape, one tassel dot. Maximum impact from minimum geometry."
            tags={["1 Shape", "Diamond", "Bold"]}
            index={19}
          >
            <Logo19 size={220} />
          </LogoCard>

          <LogoCard
            title="Two Lines Only"
            subtitle="Abstract cap — 2 strokes"
            description="Two angled lines form an abstract mortarboard. No fill, no shapes — just strokes. The most minimal version possible while still reading as a graduation cap."
            tags={["2 Lines", "Abstract", "Ultra-min"]}
            index={20}
          >
            <Logo20 size={220} />
          </LogoCard>

          <LogoCard
            title="Solid Block"
            subtitle="White on blue slab"
            description="Big solid blue rectangle with the cap and all text knocked out in white. Bold, confident, instantly readable. Looks like a brand stamp."
            tags={["Bold", "Stamp", "Inverted"]}
            index={21}
          >
            <Logo21 size={220} />
          </LogoCard>

          <LogoCard
            title="Inverted Circle"
            subtitle="White on blue disc"
            description="Big filled circle with white cap and knockout text. Classic badge feel with serious weight. Works on anything — merch, signage, app icons."
            tags={["Circle", "Heavy", "Badge"]}
            index={22}
          >
            <Logo22 size={220} />
          </LogoCard>

          <LogoCard
            title="Split Block"
            subtitle="Dark + blue two-tone"
            description="Dark navy left half, blue right half. Cap spans both. Two-tone split gives it a modern edge. Feels like a tech company that means business."
            tags={["Two-Tone", "Modern", "Edge"]}
            index={23}
          >
            <Logo23 size={220} />
          </LogoCard>

          <LogoCard
            title="Thick Stripe"
            subtitle="Name on a bold blue bar"
            description="Fat blue horizontal stripe with THOMAS MILLER punched out in white. Cap sits above, SERVICES below. Strong, assertive, impossible to miss."
            tags={["Stripe", "Assertive", "Impact"]}
            index={24}
          >
            <Logo24 size={220} />
          </LogoCard>

          <LogoCard
            title="Dark Shield"
            subtitle="Dark fill + blue edge"
            description="Solid dark navy shield with a bright blue border. White cap and text inside. Blue 'SERVICES' as accent. Authoritative, premium, protective."
            tags={["Shield", "Dark", "Authority"]}
            index={25}
          >
            <Logo25 size={220} />
          </LogoCard>

          <LogoCard
            title="Lightning Rod of Asclepius"
            subtitle="Bolt as the rod, snake wraps it"
            description="The lightning bolt IS the Rod of Asclepius — the ancient medical symbol. A golden snake wraps around it. Medicine meets power meets tech."
            tags={["Asclepius", "Lightning", "Medical"]}
            index={26}
          >
            <Logo26 size={220} />
          </LogoCard>

          <LogoCard
            title="Lightning Cross"
            subtitle="Bolt inside a medical cross"
            description="White medical cross on a solid blue circle with a lightning bolt striking through the center. Instantly says 'doctor + power'. Clean and punchy."
            tags={["Cross", "Lightning", "Bold"]}
            index={27}
          >
            <Logo27 size={220} />
          </LogoCard>

          <LogoCard
            title="Lightning Shield"
            subtitle="Bolt + caduceus wings on dark shield"
            description="Dark navy shield with a big white lightning bolt through it. Gold caduceus wings at the top. Protective, powerful, medical authority."
            tags={["Shield", "Bolt", "Wings"]}
            index={28}
          >
            <Logo28 size={220} />
          </LogoCard>

          <LogoCard
            title="Heartbeat Lightning"
            subtitle="ECG line spikes into a bolt"
            description="Flat heartbeat line that spikes up into a lightning-bolt peak. The moment where medicine meets electricity. Wide format, great for headers."
            tags={["ECG", "Heartbeat", "Spike"]}
            index={29}
          >
            <Logo29 size={220} />
          </LogoCard>

          <LogoCard
            title="Lightning Stethoscope"
            subtitle="Stethoscope tube = lightning bolt"
            description="Stethoscope where the tube from the earpieces turns into a lightning bolt leading to the chest piece. Doctor's tool electrified."
            tags={["Stethoscope", "Lightning", "Doctor"]}
            index={30}
          >
            <Logo30 size={220} />
          </LogoCard>

          <LogoCard
            title="Neural Heartbeat"
            subtitle="ECG spike + neural network branches"
            description="The heartbeat peak explodes into neural network nodes branching left and right. Where the pulse peaks, AI thinking begins. Glowing nodes on bold lines."
            tags={["Neural", "ECG", "AI Nodes"]}
            index={31}
          >
            <Logo31 size={220} />
          </LogoCard>

          <LogoCard
            title="Pulse on Dark Bar"
            subtitle="Glowing ECG on dark slab + AI brain"
            description="Dark rounded rectangle with a glowing blue heartbeat-lightning across it. Small AI brain icon at the right end. Looks like a dashboard readout."
            tags={["Dark", "Glow", "Dashboard"]}
            index={32}
          >
            <Logo32 size={220} />
          </LogoCard>

          <LogoCard
            title="Double Pulse"
            subtitle="Two spikes — MED + AI merging"
            description="Two heartbeat spikes side by side — blue for medicine, purple for AI — merging at a bright centre node. Two disciplines becoming one."
            tags={["Dual Pulse", "Merge", "Med+AI"]}
            index={33}
          >
            <Logo33 size={220} />
          </LogoCard>

          <LogoCard
            title="Pulse Circuit Board"
            subtitle="ECG with circuit traces dropping down"
            description="Bold heartbeat line with circuit-board traces dropping down from the baseline into nodes. The flat sections of the ECG become a PCB. Data under the heartbeat."
            tags={["Circuit", "PCB", "Bold"]}
            index={34}
          >
            <Logo34 size={220} />
          </LogoCard>

          <LogoCard
            title="Pulse on Blue Block"
            subtitle="White heartbeat punched through blue"
            description="Big solid blue rectangle with a white ECG-lightning carved through it. Gold peak dot. Inverted, bold, stamp-like. The strongest version of the heartbeat concept."
            tags={["Inverted", "Stamp", "Boldest"]}
            index={35}
          >
            <Logo35 size={220} />
          </LogoCard>

          <LogoCard
            title="Network Topology"
            subtitle="CS textbook node graph"
            description="Five nodes connected by edges — like a network diagram you'd see in a comp-sci lecture. Plain circles, straight lines, monospace 'Dr.' No frills, pure substance."
            tags={["Network", "CS", "Textbook"]}
            index={36}
          >
            <Logo36 size={220} />
          </LogoCard>

          <LogoCard
            title="Terminal Prompt"
            subtitle="$ whoami → Dr. Thomas Miller"
            description="Actual terminal window with traffic lights, green prompt, and commands that reveal the identity. For someone who lives in the command line."
            tags={["Terminal", "CLI", "Hacker"]}
            index={37}
          >
            <Logo37 size={220} />
          </LogoCard>

          <LogoCard
            title="Binary Tree"
            subtitle="CS data structure"
            description="A proper binary tree — root node branching into children, all square nodes with connecting edges. Looks like it belongs in an algorithms textbook."
            tags={["Tree", "Algorithm", "Structure"]}
            index={38}
          >
            <Logo38 size={220} />
          </LogoCard>

          <LogoCard
            title="Angle Brackets"
            subtitle="<D/> code tag"
            description="The letter 'D' wrapped in HTML/code angle brackets. Simple, nerdy, immediately says 'developer'. Monospace font, no decoration."
            tags={["Code", "Developer", "Markup"]}
            index={39}
          >
            <Logo39 size={220} />
          </LogoCard>

          <LogoCard
            title="Graph Paper"
            subtitle="Node graph plotted on a grid"
            description="Faint grid paper background with a node-link graph plotted on it. Like a whiteboard diagram or research paper figure. Academic and analytical."
            tags={["Grid", "Academic", "Research"]}
            index={40}
          >
            <Logo40 size={220} />
          </LogoCard>

          <LogoCard
            title="Bolt + DrThomasMillerServices"
            subtitle="Current bolt, joined text"
            description="The exact same lightning bolt from the current site with 'DrThomasMillerServices' as one word beside it. Startup/domain-name style."
            tags={["Current Bolt", "Joined", "Domain"]}
            index={41}
          >
            <Logo41 size={220} />
          </LogoCard>

          <LogoCard
            title="Bolt + Spaced Text"
            subtitle="Current bolt, readable spaces"
            description="Same lightning bolt with 'Dr Thomas Miller Services' fully spaced out. The most readable version — exactly like the current logo but with the new name."
            tags={["Current Bolt", "Spaced", "Clean"]}
            index={42}
          >
            <Logo42 size={220} />
          </LogoCard>

          <LogoCard
            title="Bolt + Two-line Stack"
            subtitle="Name on top, SERVICES below"
            description="Bigger bolt on the left. 'Dr Thomas Miller' in bold on top, 'SERVICES' in lighter caps below. Two-line lockup — works well in navbars and headers."
            tags={["Stacked", "Two-line", "Navbar"]}
            index={43}
          >
            <Logo43 size={220} />
          </LogoCard>

          <LogoCard
            title="Bolt in Circle + Text"
            subtitle="Bolt badge with stacked name"
            description="Lightning bolt inside a subtle circle badge, stacked text beside it. The circle gives the bolt icon more weight and presence. Clean lockup."
            tags={["Circle Badge", "Stacked", "Icon"]}
            index={44}
          >
            <Logo44 size={220} />
          </LogoCard>

          <LogoCard
            title="Bolt + Dr Highlight"
            subtitle="'Dr' in blue, rest in white"
            description="Lightning bolt with glow, then 'Dr' highlighted in blue followed by 'ThomasMillerServices' in white. The blue 'Dr' links visually to the bolt colour."
            tags={["Highlight", "Two-tone", "Startup"]}
            index={45}
          >
            <Logo45 size={220} />
          </LogoCard>

          <LogoCard
            title="CURRENT — Dark Background"
            subtitle="The live logo (dark mode)"
            description="Blue 'Dr' + white 'TomMillerServices'. This is the actual logo currently on the site. SVG available at /logo-dark-bg.svg"
            tags={["LIVE", "Dark BG", "Current"]}
            index={46}
          >
            <Box bg="#08090a" p={6} borderRadius="8px" display="flex" alignItems="center" justifyContent="center">
              <Logo46 size={220} />
            </Box>
          </LogoCard>

          <LogoCard
            title="CURRENT — Light Background"
            subtitle="The live logo (light mode)"
            description="Darker blue 'Dr' + dark 'TomMillerServices'. For light backgrounds. SVG available at /logo-light-bg.svg"
            tags={["LIVE", "Light BG", "Current"]}
            index={47}
          >
            <Box bg="#FAFAFA" p={6} borderRadius="8px" display="flex" alignItems="center" justifyContent="center">
              <Logo47 size={220} />
            </Box>
          </LogoCard>
        </SimpleGrid>

        {/* Notes section */}
        <Box
          bg={colorMode === "dark" ? "rgba(20, 20, 25, 0.6)" : "white"}
          border="1px solid"
          borderColor={
            colorMode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
          }
          borderRadius="16px"
          p={8}
        >
          <Text
            fontSize="lg"
            fontWeight="700"
            color={colorMode === "dark" ? "white" : "#1D1D1F"}
            mb={4}
          >
            Notes
          </Text>
          <VStack align="start" spacing={3}>
            <Text
              fontSize="sm"
              color={colorMode === "dark" ? "rgba(255,255,255,0.6)" : "#6E6E73"}
              lineHeight="1.7"
            >
              <strong style={{ color: "#007AFF" }}>Naming:</strong> "Dr Thomas Miller Services" (DTMS) is the recommended direction —
              it carries academic authority (doctorate), healthcare credibility (Paradise Lakes, Hao/SarahCare), and professional gravitas.
            </Text>
            <Text
              fontSize="sm"
              color={colorMode === "dark" ? "rgba(255,255,255,0.6)" : "#6E6E73"}
              lineHeight="1.7"
            >
              <strong style={{ color: "#007AFF" }}>Top picks with graduation cap:</strong>{" "}
              Logo 8 (Mortarboard Monogram) — cleanest, most versatile.
              Logo 10 (Professor's Mark) — most premium feel with circuit-trace tassel.
              Logo 9 (Scholar's Hex Badge) — best blockchain + academic fusion.
            </Text>
            <Text
              fontSize="sm"
              color={colorMode === "dark" ? "rgba(255,255,255,0.6)" : "#6E6E73"}
              lineHeight="1.7"
            >
              <strong style={{ color: "#007AFF" }}>Tech-first picks:</strong>{" "}
              Logo 1 (Hexagon Chain) or Logo 5 (Circuit Monogram) if you want to keep it purely tech without the academic angle.
            </Text>
            <Text
              fontSize="sm"
              color={colorMode === "dark" ? "rgba(255,255,255,0.6)" : "#6E6E73"}
              lineHeight="1.7"
            >
              <strong style={{ color: "#007AFF" }}>Next steps:</strong> Pick a direction and I can refine it — add animation,
              generate PNG/SVG exports, update brandConfig.ts, and swap out the current lightning bolt logo.
            </Text>
          </VStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Logos;
